import type { MessageSchema } from './sl'

/* English — checked against the Slovenian schema (`sl.ts`): a missing or extra key fails the type-check. */
const en = {
    assistance: {
        digit: 'Number key',
        title: "Remote assistance",
        subtitle: "View the screen and help using a virtual remote.",
        start: "Start assistance",
        connected: "Assistance is active",
        stopped: "Assistance is stopped",
        remaining: "{seconds} s remaining",
        screen_alt: "Current screen of {name}",
        waiting: "Waiting for a screenshot …",
        no_frame: "Start assistance to view the screen.",
        stale: "Snapshot is no longer fresh",
        captured: "Captured: {at}",
        refresh_hint: "The screen refreshes approximately every 4 seconds.",
        scope: "View and control the Imprion TV app. Video may appear black.",
        up: "Up",
        down: "Down",
        left: "Left",
        right: "Right",
        ok: "OK",
        back: "Back",
        home: "Home",
        channel_up: "Next channel",
        channel_down: "Previous channel",
        play_pause: "Play or pause",
        keyboard: "You can also use arrows, Enter, Backspace, Home and number keys.",
        sending: "Waiting for the key acknowledgement …",
        guest_notice: "An assistance notice is displayed on the TV.",
        stop: "End assistance",
        reconnect: "Reconnect",
        forbidden: "Remote assistance is available to administrators.",
        offline: "The device must be paired and online.",
        upgrade: "Update the Android app on the STB to view its screen.",
        failed: "Could not obtain a screenshot. Check the device connection and try again.",
        timeout: "The five-minute assistance has ended. Reconnect if needed.",
        key_failed: "The device did not confirm the key press. Assistance has stopped; reconnect if needed.",
    },
    general: {
        add:      'Add',
        edit:     'Edit',
        delete:   'Delete',
        cancel:   'Cancel',
        save:     'Save',
        close:    'Close',
        confirm:  'Confirm',
        search:   'Search …',
        loading:  'Loading …',
        saving:   'Saving',
        sign_out: 'Sign out',
        open:     'Open',
        actions:  'Options',
        none:     '—',
    },
    changelog: {
        title:     'What\'s new',
        open:      'Version and what\'s new',
        running:   'Running now',
        console:   'Console',
        screen:    'Screen',
        guest:     'Guest app',
        android:   'Android',
        api:       'API',
        new_badge: 'New',
    },
    layout: {
        open_menu:  'Open menu',
        close_menu: 'Close menu',
    },
    color_mode: {
        switch_to_light: 'Switch to light mode',
        switch_to_dark:  'Switch to dark mode',
    },
    context: {
        label:       'Customer space',
        help:        'Settings apply to the selected space.',
        placeholder: 'Select a customer',
        inactive:    '{name} (switched off)',
        not_found:   'This customer space cannot be opened.',
    },
    role: {
        admin:          'Administrator',
        editor:         'Editor',
        reception:      'Reception',
        kitchen:        'Kitchen',
        technician:     'Technician',
        housekeeping:   'Housekeeping',
        provider_admin: 'Provider administrator',
    },
    field_view: {
        open:              'Field view',
        provisioning:      'Install boxes',
        provisioning_help: 'On a laptop with the agent',
        title:             'Field',
        hotel:             'Hotel',
        full_console:      'Full console',
        refresh:           'Refresh',
        refreshed:         'updated {ago}',
        stale_data:        'No connection — showing the last data received.',
        load_failed:       'The data could not be loaded. Trying again in 30 s.',
        search:            'Search a room or device',
        clear_search:      'Clear search',
        filters:           'Filters',
        filter: {
            offline:  'Offline',
            outdated: 'Outdated',
            errors:   'Errors',
            pending:  'Waiting to pair',
        },
        empty:             'The hotel has no rooms or devices yet.',
        no_match:          'No room or device matches.',
        no_device:         'No device',
        no_room:           'No room',
        pair_here:         'Pair',
        new_device:        'New device',
        inactive:          'Switched off',
        outdated_short:    'Outdated',
        error_short:       'Error',
        not_paired:        'Not paired — waiting for a screen',
        version_screen:    'Screen {version}',
        version_app:       'App {version}',
        version_webview:   'WebView {version}',
        pending_help:      'Screens showing a code that wait in this hotel. Pair a screen on another network by typing its code (New device).',
        pending_none:      'No screen is waiting.',
        pending_gone:      'This screen is no longer waiting (paired or turned away).',
        pair_this:         'Pair this screen',
        back:              'List',
        device_gone:       'This device no longer exists.',
        rename:            'Rename',
        change_room:       'Change room',
        search_room:       'Search rooms …',
        pick_room:         'Choose a room',
        saved:             'Saved.',
        active_help:       'A switched-off device shows only the "not active" screen. A room may have several TVs; one is the main TV.',
        activated:         'The device is switched on.',
        deactivated:       'The device is switched off.',
        reset_pending:     'Reset pending',
        pair_screen:       'Pair a screen',
        time_title:        'Time and clock',
        time_now:          'Now: {zone} · {clock}',
        set_zone:          'Set the time zone',
        copy_support:      'Copy details for support',
        copied:            'The details are on the clipboard.',
        copy_failed:       'Copying failed.',
        recent:            'Recent',
        nothing_recent:    'No alerts or commands in the last 7 days.',
        resolved_at:       'resolved {at}',
        still_open:        'still open',
        confirm_collect_logs: 'Screen "{name}" will collect its log and send it to support.',
        reset_again_title: 'Really reset?',
        reset_again:       'The guest in {room} will be signed out of YouTube, Netflix, Disney+, HBO Max and Prime Video, the alarm cleared and the phone link replaced. This cannot be undone.',
        reset_confirm:     'Yes, reset',
        fact: {
            network:    'Network',
            tv:         'TV input',
            screen:     'On screen',
            screen_app: 'Screen',
            shell:      'App',
            webview:    'WebView',
            latest:     'Latest app',
            model:      'Model',
            ip:         'IP address',
            time:       'Time zone',
            reported:   'Last report',
            error:      'Last error',
        },
        summary: {
            hotel:    'Hotel',
            device:   'Device',
            room:     'Room',
            status:   'Status',
            seen:     'Last seen',
            model:    'Model',
            versions: 'Versions',
            latest:   'Available',
            network:  'Network',
            ip:       'IP',
            tv:       'TV input',
            screen:   'On screen',
            time:     'Time',
            error:    'Error',
            alerts:   'Alerts',
            id:       'ID',
        },
        pair_help:         'Type the code the TV shows, choose the room and pair.',
        pair_pending_help: 'Choose the room and pair the screen with this code.',
        code_label:        'Code on the TV',
        code_placeholder:  'ABC 123',
        code_short:        'The code has 6 characters.',
        code_unknown:      'No screen shows this code. Check it — a code lasts 24 hours.',
        code_throttled:    'Too many attempts. Wait a minute.',
        pair_target:       'Device',
        pair_into:         'Into the existing: {name}',
        pair_new:          'New device',
        pair_new_instead:  'A new device instead',
        default_name:      '{room} — TV',
        pair:              'Pair',
        paired:            'Paired: {name}',
        pair_failed:       'Pairing failed.',
    },
    locale: {
        label: 'Language',
        sl:    'Slovenščina',
        en:    'English',
    },
    success: {
        add:    'Added.',
        edit:   'Saved.',
        delete: 'Deleted.',
    },
    validation: {
        required: 'Required.',
        email:    'Invalid e-mail address.',
        too_long: 'Too long.',
        invalid:  'Invalid value.',
        url:      'The address starts with http:// or https://.',
    },
    date: {
        pick:  'Pick a date',
        clear: 'Clear the date',
    },
    time: {
        today_at:    'today {time}',
        for_a_minute: 'a minute',
        for_an_hour:  'an hour',
        tomorrow_at: 'tomorrow {time}',
    },
    auth: {
        username:          'Username',
        password:          'Password',
        password_show:     'Show password',
        password_hide:     'Hide password',
        sign_in:           'Sign in',
        or:                'or',
        failed:            'Wrong username or password.',
        too_many_attempts: 'Too many failed attempts. Try again in a few minutes.',
        welcome:           'Sign in to your space.',
    },
    passkey: {
        passkeys:        'Passkeys',
        subtitle:        'Sign in with a fingerprint, your face or a security key instead of a password.',
        sign_in:         'Sign in with a passkey',
        sign_in_failed:  'Signing in with a passkey failed.',
        add:             'Add a passkey',
        added:           'The passkey has been added.',
        failed:          'The passkey could not be added.',
        name:            'Name',
        name_help:       'The name tells the keys apart in the list, e.g. "Work laptop".',
        default_name:    'My device',
        continue:        'Continue',
        created:         'Added',
        last_used:       'Last used',
        never_used:      'Not used yet',
        empty:           'You have no passkeys yet.',
        delete:          'Delete passkey',
        confirm_delete:  'Delete the passkey "{name}"? You will no longer be able to sign in with this device without a password.',
        removed:         'The passkey has been deleted.',
        delete_failed:   'The passkey could not be deleted.',
        unavailable:     'Passkeys are not configured on this server.',
        unsupported:     'This browser does not support passkeys (a secure HTTPS connection is needed).',
    },
    password: {
        change:        'Change password',
        current:       'Current password',
        new:           'New password',
        repeat:        'Repeat the new password',
        mismatch:      'The passwords do not match.',
        changed:       'The password has been changed.',
        failed:        'The password could not be changed.',
        wrong_current: 'The current password is wrong.',
    },
    user: {
        users:               'Users',
        subtitle_tenant:     'Who can sign in to the customer space and what they may edit.',
        subtitle_provider:   'The users of every customer and the provider administrators.',
        add:                 'New user',
        edit:                'Edit user',
        delete:              'Delete user',
        name:                'Full name',
        username:            'Username',
        username_rule:       '3–32 characters: lowercase letters, digits, dot, underscore or dash.',
        username_fixed:      'The username cannot be changed.',
        username_taken:      'This username already exists.',
        email:               'E-mail',
        role:                'Role',
        role_help:           'An editor edits channels, EPG, the look and the content; an administrator also rooms, devices, the PMS link, the import and users. Reception handles guest requests and room resets without editing content; the kitchen sees only room-service orders and the menu; a technician sees only rooms and devices on a phone, pairs and moves them and sends them the safe commands.',
        space:               'Space',
        tenants:             'Spaces',
        other_tenants:       'Other spaces',
        no_memberships:      'No space',
        filter_all:          'All spaces',
        filter_provider:     'Provider administrators',
        password:            'Password',
        password_reset:      'New password (reset)',
        password_keep:       'Leave empty to keep the password.',
        password_rule:       'At least 8 characters.',
        password_too_short:  'The password must have at least 8 characters.',
        active:              'Active',
        active_help:         'An inactive user cannot sign in.',
        status:              'Status',
        status_active:       'Active',
        status_inactive:     'Inactive',
        last_login:          'Last sign-in',
        never_logged_in:     'Never',
        search:              'Search by name or e-mail …',
        show_inactive:       'Show inactive',
        empty:               'No users.',
        you:                 'You',
        cannot_delete_self:  'You cannot delete your own account.',
        confirm_delete:      'Delete the user {name}? The account is deleted in every space and can no longer sign in.',
        deleted:             'The user has been deleted.',
        delete_failed:       'The user could not be deleted.',
        last_admin:          'This is the last active administrator — at least one must remain.',
        locale:              'Language',
        locale_help:         'The application language for this user.',
        role_in_tenant:      'Role in this space',
        not_manageable:      'This user is in other spaces too — the provider edits their personal data.',
        remove:              'Remove from space',
        confirm_remove:      'Remove the user {name} from this space? If they are a member of other spaces, their account stays there; otherwise they can no longer sign in.',
        removed:             'The user has been removed from the space.',
        remove_failed:       'The user could not be removed from the space.',
        cannot_remove_self:  'You cannot remove yourself from the space.',
        add_existing:        'Add existing',
        add_existing_title:  'Add an existing user',
        add_existing_help:   'The exact username of a person who already has an account in another space.',
        add_existing_action: 'Add to space',
        exists_offer:        'A user with this username already exists. Add the existing user to this space?',
        member_added:        'The user has been added to the space.',
        member_not_found:    'There is no active user with this username.',
        already_member:      'This user is already a member of this space.',
        provider_admin:      'Provider administrator',
        provider_admin_help: 'Manages every customer, user and the provider settings; has administrator rights in every space.',
        memberships:         'Spaces',
        memberships_help:    'Which customer spaces the user works in, and with which role.',
        membership_add:      'Add space',
        membership_remove:   'Remove space',
        memberships_none:    'The user is not in any space yet.',
        memberships_required: 'Add at least one space or switch on provider administrator.',
        memberships_duplicate: 'Each space can be listed only once.',
    },
    location: {
        label:             'Place',
        label_placeholder: 'Ljubljana',
        lat:               'Latitude',
        lon:               'Longitude',
        lat_range:         'Between −90 and 90.',
        lon_range:         'Between −180 and 180.',
    },
    brand: {
        product_name: 'Imprion TV',
        provided_by:  'Provided by',
    },
    nav: {
        overview:       'Overview',
        rooms:          'Rooms',
        devices:        'Devices',
        tv:             'TV',
        content:        'Content',
        group_hotel:    'Hotel',
        connector:      'PMS link',
        import:         'Import',
        tenant_users:   'Users',
        group_provider: 'Provider',
        tenants:        'Customers',
        users:          'Users',
        android:        'Android app',
        tizen:          'Samsung TV (Tizen)',
        screen_themes:  'Themes (library)',
        milo:           'Milo',
        milo_provider:  'Milo (AI)',
        settings:       'Settings',
        requests:       'Guest requests',
        room_service:   'Room service',
        occupancy:      'Occupancy',
        feedback:       'Praise, complaints and suggestions',
    },
    errors: {
        generic_error:            'Something went wrong.',
        invalid_data:             'The data is not valid.',
        invalid_field:            'The field “{field}” is not valid.',
        not_found:                'It does not exist (any more).',
        unauthorized:             'Your session has expired. Please sign in again.',
        forbidden:                'You are not allowed to do this.',
        on_add:                   'Adding failed.',
        on_edit:                  'Saving failed.',
        on_delete:                'Deleting failed.',
        on_load:                  'Loading failed.',
        tenant_inactive:          'This customer space is switched off — signing in is not possible.',
        weather_unavailable:      'The weather forecast is not available right now.',
        in_use:                   'It is still in use and cannot be deleted.',
        in_use_list:              'It is still in use and cannot be deleted: {list}',
        not_available:            'This is not possible right now.',
        connector_not_configured: 'The PMS link is not set up.',
        connector_failed:         'The PMS did not answer or refused the request.',
        connector_failed_reason:  'The PMS refused the request: {reason}',
        epg_failed:               'The EPG source did not answer or is not an XMLTV file.',
        epg_failed_reason:        'The EPG source failed: {reason}',
        request_status:     'Somebody else has handled this request meanwhile ({status}). The list is refreshed.',
        pos_failed:         'Imprion POS did not answer.',
        pos_failed_reason:  'Imprion POS: {reason}',
        pos_not_configured: 'The Imprion POS connection is not set up (token and business unit).',
        tms_not_configured: 'The TMS (ATV Bridge) is not set up: enter the address, the API key and the task name, and switch it on.',
        tms_failed:         'The TMS (ATV Bridge) did not answer.',
        tms_failed_reason:  'TMS (ATV Bridge): {reason}',
        no_device_mac:      'The device has no MAC address the TMS could know it by. Enter it in the device’s form.',
        item_unavailable:   'The item is no longer on the menu.',
        too_many_open:      'The room has too many open requests (at most {max}).',
        stay_overlap:       'The room is already taken on these days ({arrival} – {departure}).',
        stay_state:         'The stay is already "{state}" — this is not possible now.',
        room_has_stays:     'The room cannot be deleted: it has {n} stay (a guest in the room or a reservation to come). Check it out, move or delete it on the Occupancy page. | The room cannot be deleted: it has {n} stays (a guest in the room or reservations to come). Check them out, move or delete them on the Occupancy page.',
        stay_dates:         'The dates do not allow this today: a check-in is possible from the arrival day to the departure day, and a guest in the room cannot have the arrival moved into the future.',
    },
    settings: {
        title:             'Settings',
        subtitle:          'Your account, language and sign-in.',
        profile:           'Profile',
        profile_subtitle:  'The name and e-mail address of your account.',
        account:           'Account',
        tenant:            'Customer space',
        tenants:           'Customer spaces',
        role:              'Role',
        locale_help:       'The console’s language.',
        provider:          'Provider',
        provider_subtitle: 'The provider’s name, shown by the console and the screens as the product’s publisher, and Leviathan’s address.',
        provider_name:     'Provider name',
        leviathan_url:      'Leviathan address',
        leviathan_url_help: 'IT TEAM’s Leviathan (STB management): “Open in Leviathan” on a device opens its terminal list. Empty = the default address.',
        leviathan_url_rule: 'The address starts with https://, without ? or #.',
        version:           'Version',
    },
    mail_settings: {
        title:              'Mail',
        subtitle:           'The SMTP server the console sends e-mail through — for now the device alerts.',
        source_console:     'From the console',
        source_env:         'From the server (.env)',
        source_none:        'Not set',
        env_note:           'The server’s setting (.env) is in effect. Once you save settings here, these are used — as a whole.',
        none_note:          'Mail is not set: alerts are only recorded and shown in the console.',
        console_note:       'The console’s setting is in effect; the server’s (.env) is not used.',
        env_password_set:   'The password is set on the server.',
        env_password_unset: 'No password on the server (no sign-in).',
        set_in_console:     'Set in the console',
        use_env:            'Use the server’s settings',
        remove:             'Remove the settings',
        use_env_confirm:    'Remove the mail settings from the console? The server’s settings (.env) apply again.',
        remove_confirm:     'Remove the mail settings? Alerts are then only recorded, not e-mailed.',
        removed:            'The mail settings were removed.',
        host:               'Server',
        host_help:          'A host name or IP address, without a port or smtp://.',
        host_rule:          'A host name (letters, digits, - and .) or an IP address.',
        port:               'Port',
        port_help:          '587 = STARTTLS, 465 = TLS from the start.',
        user:               'User',
        user_help:          'Empty = no sign-in.',
        from:               'Sender',
        from_help:          "An address, a name allowed: Imprion TV <tv-no-reply{'@'}imprion.io>. It must be the user’s mailbox or an alias it may send as.",
        from_rule:          'Enter an e-mail address, a name allowed (Name <address>).',
        password:           'Password',
        password_help:      'Stored encrypted and never shown.',
        password_set:       'Stored {hint}',
        password_unset:     'No password',
        password_replace:   'Replace password',
        password_keep:      'Keep the stored one',
        password_required:  'With a user, a password is required.',
        password_user_none: 'Without a user no password is used (it is removed on save).',
        test_title:         'Test mail',
        test_help:          'Sends a short message through the saved settings in effect.',
        test_unsaved:       'Your changes are not saved yet — the saved settings are tested.',
        test_to:            'Recipient',
        test_send:          'Send a test mail',
        test_ok:            'The test mail was sent at {time} to {to}.',
        test_ok_source:     'Settings used — {source}',
        test_failed:        'The test mail was not sent',
        server_said:        'Server: {message}',
        failure: {
            not_configured:     'Mail is not set.',
            unreachable:        'The server cannot be reached — check the host name and port.',
            timeout:            'The server did not answer in time.',
            tls:                'The secure connection (TLS) failed — the server’s certificate does not match its name or is not valid.',
            no_starttls:        'The server offers no STARTTLS, the password would not be protected — use port 587 with STARTTLS, or 465.',
            auth_failed:        'Sign-in failed — the user or password is wrong.',
            auth_required:      'The server requires a sign-in — enter a user and password.',
            sender_rejected:    'The server does not allow sending from this sender address. The sender must be the user’s mailbox or an alias it may send as (Mailcow: the mailbox’s aliases, or sender_acl).',
            recipient_rejected: 'The server refused the recipient — check the address.',
            rejected:           'The server refused the message.',
        },
    },
    tenant: {
        tenants:          'Customers',
        subtitle:         'Every hotel has its own space: rooms, devices, channels, content and users.',
        add:              'New customer',
        edit:             'Edit customer',
        delete:           'Delete customer',
        name:             'Name',
        name_placeholder: 'Grill Oaza',
        slug:             'Slug',
        slug_help:        'Lowercase letters, digits and hyphens (2–40). Made from the name.',
        slug_rule:        'Only lowercase letters, digits and hyphens, 2–40 characters.',
        slug_taken:       'Another customer already uses this slug.',
        timezone:         'Time zone',
        timezone_help:    'The programme, the EPG and the clocks on the screens run on this time.',
        currency:         'Currency',
        currency_help:    'An ISO 4217 code, e.g. EUR.',
        currency_rule:    'Three letters, e.g. EUR.',
        domain:           'Own domain',
        domain_help:      'E.g. tv.hotel.si — the console sign-in and the screens on this domain belong to this customer. Empty = none.',
        domain_rule:      'Not a valid domain.',
        location:         'Weather town',
        location_help:    'The screens’ weather forecast is for this town.',
        active:           'Active',
        active_help:      'A switched-off customer cannot sign in; its screens show “not active”.',
        status:           'Status',
        status_active:    'Active',
        status_inactive:  'Switched off',
        rooms:            'Rooms',
        devices:          'Devices',
        users:            'Users',
        connector:        'PMS',
        created:          'Created',
        open:             'Open space',
        confirm_delete:   'Delete customer {name}? Its users will no longer be able to sign in and its screens are signed out.',
        deleted:          'The customer is deleted.',
        delete_failed:    'The customer could not be deleted.',
        empty:            'There are no customers yet.',
        empty_help:       'Add the first hotel, then open its space.',
        search:           'Search by name or slug …',
        activated:        'Customer {name} is switched on.',
        deactivated:      'Customer {name} is switched off.',
    },
    language: {
        sl: 'Slovenščina',
        en: 'English',
        de: 'Deutsch',
        it: 'Italiano',
        hr: 'Hrvatski',
        hu: 'Magyar',
    },
    localized: {
        editing_in:     'Editing in',
        field_language: 'Field language',
        show_all:       'All languages',
        all_translated: 'All translated',
        missing:        '{n} translation missing | {n} translations missing',
        lang_missing:   '{name} — missing in {n} field | {name} — missing in {n} fields',
        lang_complete:  '{name} — all translated',
        mark_filled:    '{name} — filled in',
        mark_fallback:  '{name} — not translated, the screen shows {fallback}',
        mark_empty:     '{name} — empty',
        translate_missing:        'Translate missing',
        translating:              'Milo is translating into {n} language … | Milo is translating into {n} languages …',
        translated:               'Translated: {texts} into {languages} — review and save.',
        n_texts:                  '{n} text | {n} texts',
        n_languages:              '{n} language | {n} languages',
        translate_partly:         'Some texts are still untranslated.',
        translate_none:           'Milo translated no text.',
        translate_failed:         'The translation failed. Try again.',
        translate_no_provider:    'Milo has no AI provider set up — ask your provider.',
        translate_quota:          'Milo\'s limit for today is used up — try again tomorrow.',
        translate_provider_error: 'Milo cannot connect right now. Try again.',
        translate_bad_answer:     'Milo did not return a readable translation. Try again.',
    },
    status: {
        online:   'Online',
        offline:  'Offline',
        unpaired: 'Not paired',
    },
    usage: {
        guest:   'Guest room',
        signage: 'Public screen',
    },
    orientation: {
        auto:             'Automatic',
        landscape:        'Landscape',
        portrait:         'Portrait',
        portrait_flipped: 'Portrait (flipped)',
    },
    platform: {
        android: 'Android',
        tizen:   'Samsung Tizen',
        webos:   'LG webOS',
        web:     'Browser',
    },
    connector_type: {
        none:        'No PMS',
        imprion_pms: 'Imprion PMS',
        bentral:     'Bentral',
    },
    bentral_status: {
        confirmed:    'Confirmed',
        paid:         'Paid',
        paid_part:    'Partly paid',
        notpaid:      'Not paid',
        pending:      'Pending',
        available:    'Enquiry',
        paying:       'Payment in progress',
        noresponse:   'No response',
        noshow:       'No-show',
        notavailable: 'Not available',
        canceled:     'Cancelled',
    },
    capability: {
        stay:           'Stay',
        stays:          'Occupancy (range)',
        bill:           'Bill',
        services:       'Services',
        booking:        'Bookings',
        room_service:   'Room service',
        program:        'Programme',
        program_signup: 'Programme sign-ups',
        voucher:        'Voucher',
        nearby:         'Nearby partners',
        nearby_booking: 'Partner bookings',
        venue:          'Halls',
    },
    epg_status: {
        ok:       'OK',
        unmapped: 'No EPG',
        no_data:  'No data',
    },
    stream_health: {
        ok:                   'Working',
        down:                 'Down',
        down_for:             'Down for {for}',
        never:                'Not checked yet',
        not_checkable:        'Cannot be checked',
        down_since:           'no signal since {at}',
        checked:              'checked {ago}',
        disabled_not_checked: 'a disabled channel is not checked automatically',
        error_http:           '{status} at the source',
        error_timeout:        'the source did not answer',
        error_unreachable:    'no connection to the source',
        error_redirects:      'too many redirects',
        error_empty_playlist: 'empty playlist',
        error_not_http:       'the source is not http(s)',
        error_resolve_failed: 'the stream could not be looked up',
        error_not_stream:     'the source is no stream (a web page)',
    },
    stream_check: {
        badge_ok:        'Works',
        badge_down:      'Not working',
        badge_relay:     'Not on the Android boxes — the server converts it',
        badge_android:   'Not on the Android boxes',
        badge_unknown:   'Not checked',
        never:           'Not checked yet — it is checked at night or with “Check now”.',
        checked:         'checked {ago}',
        format_unknown:  'format not known',
        check_now:       'Check now',
        checked_ok:      '{name}: checked.',
        platform: {
            android_web:    'Android box (radio, videos)',
            android_native: 'Android box (TV channels)',
            tizen:          'Samsung',
            webos:          'LG',
        },
        play: {
            yes:     'plays',
            no:      'does not play',
            relay:   'the server converts it to MP3',
            unknown: 'not known',
        },
        code: {
            music_failed: 'the station did not play',
            native:       'the box’s player failed',
        },
    },
    auto_hide: {
        badge:          'Hidden automatically (not reachable since {since})',
        badge_detail:   'Guests do not see it until it is reachable again. Last error: {reason}. Next check {next}.',
        next_unknown:   'soon',
        title:          'Automatic hiding',
        subtitle:       'Channels and stations that cannot be reached are hidden from the guests and come back when they work again.',
        switch:         'Hide unreachable channels and stations automatically',
        switch_help:    'Hidden only when a stream fails three checks in a row over at least 15 minutes (or two, when a screen reports it failing). It comes back at the first check that works. Never switches on what was switched off by hand.',
        mail:           'Send an e-mail when something is hidden or comes back',
        mail_help:      'Recipients and spacing come from the alert settings (Devices › Settings).',
        mail_needs:     'For e-mail, switch alerts on and add recipients in Devices › Settings.',
        admin_only:     'An administrator can change this setting.',
        how:            'While it is on, the server checks the enabled channels and stations about every 45 minutes, hidden ones every 10 minutes. A screen playing a hidden station or channel keeps playing; an alarm set to a hidden station rings with the house\'s first station or a tone. Channel numbers do not shift.',
        saved_on:       'Automatic hiding is on.',
        saved_off:      'Automatic hiding is off — everything hidden is visible again.',
        saved:          'Saved.',
        hidden_now:     'Hidden now: {n}',
        none_hidden:    'Nothing is hidden right now.',
        events:         'Hiding history',
        events_empty:   'Nothing was hidden or came back in this period.',
        change_hidden:  'hidden',
        change_shown:   'back',
        reason_working: 'works again',
        reason_http:    'the source answers with an error',
        reason_option_off: 'automatic hiding switched off',
        back:           '{name} is reachable again and visible to the guests.',
        still:          '{name} is still not reachable.',
    },
    stream_type: {
        auto: 'Automatic',
        hls:  'HLS',
        ts:   'MPEG-TS',
    },
    proxy: {
        auto:        'Automatic',
        always:      'Always through the server',
        never:       'Never',
        auto_help:   'http:// sources go through the Imprion TV server, https:// ones play directly.',
        always_help: 'Every stream goes through the server — the provider’s address and token never reach a screen.',
        never_help:  'The screen plays the source directly (the token is in the address too).',
    },
    adult: {
        hidden:      'Hidden',
        hidden_help: 'Adult channels are not shown on the room screen.',
        pin:         'Behind a PIN',
        pin_help:    'The channels are listed but open only with the PIN (default).',
        shown:       'Shown',
        shown_help:  'Adult channels are listed like every other.',
    },
    screen: {
        home:         'Home',
        letter:       'Welcome letter',
        program:      'Programme',
        services:     'Services',
        local:        'Area',
        house:        'House',
        bill:         'Bill',
        booking:      'Bookings',
        music:        'Music',
        alarm:        'Alarm',
        tv:           'Television',
        room_service: 'Room service',
        nearby:       'Partners',
        invitations:  'Invitations',
        cast:         'Apps',
        milo:         'Milo',
        requests:     'Your requests',
        room_guide:   'Your room',
        feedback:     'Praise, complaints and suggestions',
    },
    display_theme: {
        default: 'Default (dark)',
        dark:    'Dark',
        light:   'Light',
        auto:    'Automatic (by time)',
    },
    typeface: {
        default:            'Default',
        system:             'System',
        trirong_montserrat: 'Trirong + Montserrat',
    },
    radius: {
        default: 'Default',
        rounded: 'Rounded',
        square:  'Square',
    },
    invitation_kind: {
        dinner:   'Dinner',
        event:    'Event',
        tasting:  'Tasting',
        wellness: 'Wellness',
        offer:    'Offer',
        other:    'Other',
    },
    place_kind: {
        sight:      'Sight',
        restaurant: 'Restaurant',
        activity:   'Activity',
        shop:       'Shop',
        other:      'Other',
    },
    music_source: {
        stream: 'Stream (radio)',
        file:   'File',
    },
    music_audience: {
        guest:      'Rooms',
        house:      'Public areas',
        guest_help: 'Stations the guest picks on the room screen.',
        house_help: 'For public screens — needs a public-performance licence.',
    },
    slide_kind: {
        welcome:        'Welcome',
        program:        'Programme of the day',
        weather:        'Weather',
        weather_hourly: 'Weather by hour',
        services:       'Services',
        local:          'Area',
        hours:          'Opening hours',
        poster:         'Poster',
        video:          'Video',
    },
    program_source: {
        tenant:         'In the console',
        tenant_help:    'You enter the programme of the day on the Programme tab.',
        connector:      'From the PMS',
        connector_help: 'The programme comes from the PMS (needs a link with the Programme capability).',
    },
    order: {
        up:   'Move up',
        down: 'Move down',
    },
    asset: {
        upload:            'Upload',
        choose:            'Choose',
        remove:            'Remove',
        choose_title:      'Choose a file',
        count:             '{n} file | {n} files',
        empty:             'No files uploaded yet.',
        confirm_delete:    'Delete the file “{name}”?',
        kind_image:        'Image',
        kind_video:        'Video',
        too_large:         'The file is too large.',
        too_large_named:   '{name}: the {kind} is too large (at most {limit}).',
        unsupported:       'This kind of file cannot be uploaded.',
        unsupported_named: '{name}: this kind of file cannot be uploaded (JPEG, PNG, WebP, SVG; MP4, WebM).',
        webm_pending:      'WebM: preparing…',
        webm_ready:        'WebM: ready ({size})',
        webm_failed:       'WebM: error',
        webm_failed_help:  'The conversion failed: {error}',
        webm_help:         'For the Android boxes (ohm) a video is converted to WebM automatically; Samsung and LG play the original.',
        convert_again:     'Convert again',
        convert_started:   'The conversion to WebM has started.',
    },
    overview: {
        title:                     'Overview',
        subtitle:                  'Devices, rooms and the TV guide of the hotel.',
        online:                    'Online',
        offline:                   'Offline',
        of_total:                  'of {total} devices',
        unpaired_hint:             'not paired: {n}',
        inactive_hint:             'inactive devices: {n}',
        rooms_without_device:      'Rooms without a device',
        rooms_without_device_help: 'Rooms without an active device — the TV there shows nothing.',
        of_rooms:                  'of {total} rooms',
        without_epg:               'Channels without EPG',
        of_channels:               'of {total} channels',
        epg_updated:               'EPG updated',
        providers:                 '{n} provider | {n} providers',
        outdated_apps:             'Outdated apps',
        all_rooms:                 'All rooms',
        no_rooms:                  'There are no rooms yet.',
        every_room_has_device:     'Every room has an active device.',
        no_device:                 'No device',
        no_active_device:          'None active',
        epg:                       'TV guide (EPG)',
        no_providers:              'There is no EPG provider yet.',
        updated_ago:               'updated {ago}',
        never_updated:             'never updated',
        connector:                 'PMS link',
        connector_ok:              'last successful call {ago}',
        connector_open:            'Open the link',
        no_signal:                 'Channels without signal',
        no_signal_help:            'Their source does not answer — guests cannot watch them.',
        every_channel_has_signal:  'Every enabled channel has a signal.',
        all_channels:              'All channels',
    },
    pending: {
        title:             'Waiting for confirmation ({n})',
        badge:             'Waiting for confirmation: {n}',
        badge_open:        'Open devices',
        code_on_screen:    'code on the screen',
        ip:                'The IP address the screen called from',
        unknown_device:    'Unknown device',
        no_tenant:         'No space',
        same_network:      'Same network',
        same_network_help: 'The screen is on the network an admin of this space works from.',
        suggested:         'Probably: {name}',
        suggested_short:   'suggested',
        confirm:           'Confirm',
        dismiss:           'Dismiss',
        dismissed:         'Dismissed — the screen will show a new code.',
        approve_title:     'Confirm screen',
        approve_help:      'The screen with code {code} will be paired with this hotel.',
        approve_failed:    'Confirming failed.',
        approved:          'The screen is paired.',
        target:            'Device',
        new_device:        'New device',
        gone:              'This screen is gone — it started again with a new code.',
    },
    room: {
        rooms:               'Rooms',
        subtitle:            'The hotel’s rooms with their devices and their link to the PMS room.',
        add:                 'New room',
        edit:                'Edit room',
        delete:              'Delete room',
        name:                'Name',
        name_help:           'In at least one language; the screen shows the guest’s.',
        name_required:       'Enter the name in at least one language.',
        location:            'Location',
        location_help:       'Free text, e.g. “Forest” or “2nd floor”.',
        external_id:         'PMS ID',
        external_id_help:    'The room in the PMS — the link finds the guest by it. Empty = none.',
        order:               'Order',
        order_help:          'A lower number comes first.',
        devices:             'Devices',
        active_device:       'Active device',
        inactive_device:     'Inactive device',
        primary_device:      'Main TV',
        search:              'Search by name, location or ID …',
        only_without_device: 'Only without an active device',
        empty:               'There are no rooms yet.',
        empty_help:          'Add rooms by hand or import them from the old system.',
        confirm_delete:      'Delete room {name}?',
        wifi_password:       "This room's Wi-Fi password",
        wifi_password_help:  "If the hotel uses one network with a password per room (a VLAN per room). Empty = the hotel's password.",
        wifi_password_placeholder: "The hotel's password",
        wifi_column:         'Wi-Fi password',
        wifi_hotel:          "The hotel's password",
        wifi_show:           'Show password',
        wifi_hide:           'Hide password',
    },
    room_type: {
        title:          'Room types',
        subtitle:       'Write "Your room" for a room type and add to it for a single room where needed.',
        add:            'New type',
        edit:           'Edit room type',
        delete:         'Delete room type',
        name:           'Name',
        name_help:      'E.g. "Double room" or "Apartment"; in at least one language.',
        empty:          'No room types yet. Without them "Your room" applies to every room at once or to each room on its own.',
        rooms_n:        '{n} room | {n} rooms',
        field:          'Room type',
        field_help:     '"Your room" takes the content of this type.',
        none:           'No type',
        in_use:         'The type has {n} room — give it another type first. | The type has {n} rooms — give them another type first.',
        in_use_now:     'The type has rooms meanwhile — give them another type first.',
        confirm_delete: 'Delete room type {name}? Its "Your room" content is deleted too.',
    },
    room_wifi: {
        placeholder: 'Room;Password\nRoom 101;Password-101\nRoom 102;Password-102',
        import:      'Import Wi-Fi passwords',
        title:       'Import Wi-Fi passwords per room',
        description: "One line per room: the room's name or PMS ID in the first column, its password in the second. An empty password brings back the hotel's.",
        drop:        'CSV file',
        drop_help:   'Separated by ; or , — or paste a table from Excel below.',
        text:        'Lines',
        text_help:   'The first line may be a header (Room;Password). Extra columns are ignored.',
        check:       'Check',
        apply:       'Import ({n})',
        rows:        '{n} line | {n} lines',
        to_write:    '{n} change | {n} changes',
        unknown_n:   '{n} unknown | {n} unknown',
        invalid_n:   '{n} invalid | {n} invalid',
        reveal:      'Show passwords',
        line:        'line {n}',
        line_column: 'Line',
        change:      'Change',
        change_set:   'New password',
        change_clear: "The hotel's password",
        change_same:  'No change',
        skipped:     'These lines are not imported',
        unknown:     'No such room',
        problem_duplicate: 'The room is on an earlier line too',
        problem_ambiguous: 'Several rooms have this name — use the PMS ID',
        problem_too_long:  'The password is longer than 120 characters',
        problem_columns:   'The password column is missing',
        dry_note:    'This is only a check — nothing is written yet. Click "Import" to write it.',
        unreadable:  'The lines could not be read (empty, or more than 2000 lines).',
        done:        'Wi-Fi passwords imported ({n}).',
    },
    monitoring: {
        group_when:              'When to alert',
        group_mail:              'Sending',
        status: {
            online:   'Online',
            stale:    'Not responding',
            offline:  'Offline',
            unpaired: 'Not paired',
        },
        signal: {
            excellent: 'Excellent signal',
            good:      'Good signal',
            fair:      'Fair signal',
            weak:      'Weak signal',
            unknown:   'Wi-Fi',
        },
        network: {
            wifi:     'Wi-Fi',
            ethernet: 'Cable',
            other:    'Other',
            none:     'No network',
            unknown:  'Unknown',
        },
        update_mode: {
            device_owner: 'silent (Device Owner)',
            system:       'silent (system)',
            technician:   'technician',
        },
        screens: {
            home:         'Home',
            letter:       'Welcome letter',
            program:      'Programme',
            services:     'Services',
            local:        'Surroundings',
            house:        'Your stay',
            bill:         'Bill',
            booking:      'Bookings',
            music:        'Music',
            alarm:        'Alarm',
            tv:           'TV',
            room_service: 'Room service',
            nearby:       'Partners',
            invitations:  'Invitations',
            cast:         'Apps',
            milo:         'Milo',
            requests:     'Your requests',
            room_guide:   'Your room',
            feedback:     'Praise, complaints and suggestions',
        },
        alert_kind: {
            offline:        'Offline',
            playback_error: 'Playback error',
        },
        ops: {
            reload:         'Reload screen',
            reboot:         'Reboot',
            update_now:     'Update now',
            reset_guest:    'Reset for the next guest',
            set_timezone:   'Time zone',
            set_clock:      'Clock format',
            open_tech_menu: 'Technician menu',
            collect_logs:   'Collect logs',
            capture_screen: 'Capture screen',
            remote_key: 'Remote key press',
            wake_tv:        'Wake TV (check-in)',
            tv_play:        'Guest: channel',
            tv_stop:        'Guest: stop TV',
            music_play:     'Guest: radio',
            music_stop:     'Guest: stop radio',
            volume_set:     'Guest: volume',
            alarm_set:      'Guest: alarm',
            alarm_cancel:   'Guest: cancel alarm',
        },
        command_state: {
            queued:    'Waiting',
            delivered: 'Sent',
            done:      'Done',
            failed:    'Failed',
            expired:   'Expired',
        },
        confirm: {
            reload:         'The screen "{name}" will load again. What the guest is watching stops for a moment.',
            reboot:         'The device "{name}" will restart (about a minute without picture).',
            update_now:     'The device "{name}" will check at once and silently install a newer app version, if there is one.',
            reset_guest:    'The device "{name}" will be reset for the next guest (YouTube, Netflix, Disney+, HBO Max and Prime Video sign-out, language, alarm, a new phone link).',
            set_timezone:   'The time zone of "{name}" will be set to {tz}. The screen sets the hotel’s zone again on its next start.',
            set_clock:      'The screen "{name}" will show times in the {clock}-hour format.',
            open_tech_menu: 'The technician menu opens on "{name}" (PIN asked). This is for a technician at the TV.',
            collect_logs:   'The screen "{name}" will collect and send its logs; download them below.',
            update_webview: 'The device "{name}" will download and silently install a newer WebView at once, if there is one (a newer app first, if there is one). The screen goes dark for a moment and starts again.',
        },
        update_webview:      'Update WebView now',
        blocked_role:        'You are not allowed to do this.',
        blocked_unpaired:    'The device is not paired.',
        blocked_cap:         'The device does not support this (missing "{cap}").',
        free_of:             '{free} of {total} GB free',
        outdated:            '{version} available',
        update_available:    '→ {version}',
        outdated_line:       'App {version}, {latest} available',
        no_telemetry:        'The screen has not sent device data yet (an older version or a browser).',
        timeline:            'Timeline',
        range_24h:           '24 h',
        range_7d:            '7 days',
        no_errors:           'No playback errors in this period.',
        commands:            'Commands',
        apply:               'Set',
        send:                'Send',
        sent:                'Sent: {op}.',
        recent_commands:     'Recent commands',
        no_commands:         'No commands in the last 7 days.',
        guest_phone:         'guest’s phone',
        system:              'System',
        download_logs:       'Download logs',
        download:            'Download',
        log_bundles:         'Device logs',
        no_bundles:          'No logs collected (kept 7 days).',
        bundle_line:         '{entries} screen entries · {source} · {size}',
        log_source: {
            logcat_all: 'system log',
            logcat_own: 'app log',
            none:       'no system log',
        },
        seen_ago:            'last seen {ago}',
        screen_app:          'Screen',
        shell:               'App',
        showing:             'Showing',
        network_label:       'Network',
        addresses:           'IP addresses',
        uptime:              'Up for',
        app_uptime:          'app {up}',
        ram:                 'Memory',
        storage:             'Storage',
        webview:             'WebView',
        time:                'Time',
        alarm:               'Alarm',
        alarm_on:            'set',
        alarm_off:           'not set',
        last_reset:          'Last reset',
        reported:            'Data from',
        details:             'Details',
        errors_badge:        'Errors',
        signal_column:       'Signal',
        filter_errors:       'With errors (last hour)',
        filter_outdated:     'Outdated app',
        card_title:          'Screen monitoring',
        card_subtitle:       'All devices: {total}',
        all_devices:         'All devices',
        all_well:            'Every screen is working.',
        alerts_title:        'Alerts',
        alerts_subtitle:     'E-mail when a screen goes offline or fails to play.',
        mail_not_configured: 'The server has no mail (SMTP) set up: alerts are only recorded and shown here.',
        alerts_enabled:      'Alerts',
        alerts_enabled_help: 'Switching them off closes the open alerts.',
        offline_minutes:     'Offline after (min)',
        offline_minutes_help: 'This many minutes without contact and a device counts as offline.',
        playback_errors:     'Playback errors',
        playback_errors_help: 'Alert when a screen fails to play a channel or radio.',
        stream_digest:       'Morning report on channels and stations',
        stream_digest_help:  'After the nightly check, an e-mail with the channels and stations that do not work and those with the most errors on the screens.',
        digest_minutes:      'At most one e-mail per (min)',
        digest_minutes_help: 'Alerts are gathered into one message.',
        recipients:          'Recipients',
        recipients_help:     'Up to 10 e-mail addresses; separate them with Enter, a comma or a space.',
        recipients_rule:     'Each recipient must be an e-mail address (at most 10).',
        recipients_placeholder: "tech{'@'}hotel.com",
        admin_only:          'Alert settings are edited by an admin.',
    },
    device: {
        tab_list:                'List',
        tab_streams:             'Playback',
        tab_settings:            'Settings',
        tab_provisioning:        'Installation',
        devices:        'Devices',
        subtitle:       'Room TVs and public screens: status, signal, what they show, app and remote commands.',
        add:            'Add device',
        add_help:       'A device without pairing (e.g. an STB not installed yet) — a screen is paired into it later.',
        edit:           'Edit device',
        delete:         'Delete device',
        name:           'Name',
        usage:          'Usage',
        room:           'Room',
        room_required:  'Choose a room.',
        no_room:        'No room',
        venue:          'PMS hall',
        venue_help:     'For a screen at a hall’s door: the hall’s PMS ID. Empty = a plain public screen.',
        orientation:    'Orientation',
        playlist:       'Signage loop',
        no_playlist:    'No loop',
        mac:            'MAC',
        mac_rule:       'A MAC is six pairs of hex digits, e.g. 1C:A0:EF:D8:3D:8B.',
        mac_copy:       'Copy MAC',
        mac_copied:     'MAC copied.',
        mac_copy_failed: 'The MAC could not be copied – type it in.',
        mac_missing:    'MAC not reported',
        leviathan_open: 'Open in Leviathan',
        leviathan_copied: 'MAC copied – paste it into the MAC search in Leviathan',
        serial:         'Serial number',
        reported_help:  'The device overwrites them when it reports.',
        cast_name:      'TV name on phones',
        cast_name_help: 'The name a phone shows for AirPlay, Smart View and Cast (on a Samsung: Settings › General › System Manager › Device Name). The guest picks it on the phone. An Android box names itself, by the setting “Device name on the TV” (“Imprion TV” by default).',
        notes:          'Notes',
        active:         'Active',
        active_short:   'Active',
        active_help:    'A room (a suite) may have several TVs; all are active, one of them is the main TV.',
        name_place_help: 'In a room with several TVs, a short place name such as “Bedroom” or “Living room” — the guest picks the TV by it on the phone.',
        primary:         'Main TV in the room',
        primary_short:   'main',
        make_primary:    'Make it the main TV',
        primary_set:     '{name} is now the main TV in the room.',
        primary_help:    'The guest’s phone and Milo use the main TV unless the guest picks another; at check-in the main TV comes on (or all of them, as set under HDMI-CEC).',
        another_tv:      'Room {room} already has a TV ({names}). This one becomes another TV of the room and the others stay on — enter a short place name such as “Bedroom”.',
        place_example:   'e.g. Bedroom',
        status:         'Status',
        software:       'Device and app',
        platform:       'Platform',
        model:          'Model',
        app_version:    'App',
        system_version: 'Shell',
        lan_ip:         'LAN IP',
        public_ip:      'Public IP',
        last_seen:      'Last seen',
        never_seen:     'Never',
        paired_at:      'Paired',
        pairing_code:   'Pairing code',
        capabilities:   'Capabilities',
        volume_path:    'Volume from the phone',
        volume_path_key:    'To the TV (HDMI-CEC)',
        volume_path_stream: 'The box\'s own volume',
        search:         'Search by name, room, MAC …',
        all_usages:     'All usages',
        all_statuses:   'All statuses',
        inactive_only:  'Inactive only',
        empty:          'There are no devices yet.',
        how_title:      'How to add a TV',
        how_help:       'Start the Imprion TV app on the TV or open {address} — it shows a code and appears here among the devices waiting for confirmation.',
        activated:      '{name} is active.',
        deactivated:    '{name} is no longer active.',
        unpair:         'Unpair',
        unpaired:       'The device is unpaired — the screen will show a new code.',
        confirm_unpair: 'Unpair device {name}? The screen loses its key and must be confirmed again.',
        confirm_delete: 'Delete device {name}? The screen loses its key.',
        logs:           'Log',
        logs_title:     'Log: {name}',
        logs_help:      'The screen’s latest messages (kept 7 days).',
        logs_empty:     'No messages.',
    },
    hdmi_cec: {
        title:                   'HDMI-CEC',
        subtitle:                'How the box drives the TV over the HDMI cable: input and power.',
        group_input:             'Input and name',
        group_check_in:          'At a guest’s check-in',
        help:                    'The box takes the TV back to our input on every key of our remote and after a reset for the next guest. It never switches a TV in standby on — except at a guest’s check-in, if you turn that on below. Only on devices with HDMI-CEC (the system build of the app).',
        input_return:            'Always return to our input',
        input_return_help:       'When a guest switches to another source (e.g. HDMI 2), the box takes it back to our input after this time. “Immediately” is for public and lobby screens.',
        wake_on_check_in:        'Switch the TV on at check-in',
        wake_on_check_in_help:   'At check-in the TV comes on with the welcome and the guest’s name.',
        idle_standby:            'Switch off if nobody uses it within',
        idle_standby_help:       'After a check-in: if nobody presses a key, the TV goes back to standby.',
        device_name:             'Device name on the TV',
        device_name_help:        'The name the box gives itself to the TV (its list of inputs) and to phones (Cast, YouTube). At most 14 characters without accents; older TVs do not show it.',
        wake_scope:              'On guest check-in, switch on',
        wake_scope_help:         'For rooms with several TVs (suites). A room with one TV always switches it on.',
        wake_scope_values: {
            primary: 'The main TV',
            all:     'Every TV in the room',
        },
        admin_only:              'The HDMI-CEC settings are the admin’s.',
        never:                   'Never',
        minutes:                 '{minutes} min',
        input_return_values: {
            off:       'Off',
            after_5:   'After 5 min',
            after_10:  'After 10 min',
            after_30:  'After 30 min',
            after_60:  'After 60 min',
            immediate: 'Immediately',
        },
        wake_values: {
            off:    'Off',
            manual: 'Only on manual check-in',
            all:    'Also on automatic check-in',
        },
        device_name_values: {
            imprion: 'Imprion TV',
            room:    'Room name',
        },
        osd_name:                'Name on the TV: {value}',
        device_override:         'Return to our input',
        device_override_help:    'For this device instead of the house’s setting (e.g. “Immediately” for a lobby screen).',
        as_house:                'As the house',
        drawer_title:            'HDMI-CEC',
        tv_input_label:          'TV input: {value}',
        tv_input_unknown:        'unknown',
        through_device:          ' (through a device)',
        tv_vendor:               'vendor {value}',
        tv_name:                 'name {value}',
        address:                 'address {value}',
        supported:               'supported',
        unsupported:             'not supported',
        no_report:               'supported, no report yet',
        disabled_android:        'switched off in Android',
        active_source:           'active source: {value}',
        active_unknown:          'unknown',
        yes:                     'yes',
        no:                      'no',
        tv:                      'TV: {value}',
        returns:                 'returns: {value}',
        last:                    'last switch {at} ({reason} → {result})',
        action: {
            take:    'Input taken',
            standby: 'TV standby',
        },
        reason: {
            key:             'remote key',
            reset:           'guest reset',
            policy:          'always return',
            check_in:        'guest check-in',
            idle:            'not used',
            tv_off:          'TV switched off',
            tv_on:           'TV switched on',
            tv_other_source: 'TV switched to another source',
            tv_our_input:    'TV back on the STB',
        },
        result: {
            taken:      'switched',
            already:    'already ours',
            tv_standby: 'TV in standby — not woken',
            tv_unknown: 'TV does not answer',
            sent:       'sent',
            not_active: 'another source — left on',
            limited:    'hourly limit',
            failed:     'failed',
        },
        tv_power: {
            on:      'on',
            standby: 'standby',
            unknown: 'not answering',
        },
    },
    device_events: {
        title:       'Device events',
        filter:      'Event kind',
        all_kinds:   'All kinds',
        days:        '{days} day | {days} days',
        empty:       'No events in this period.',
        error:       'The events could not be loaded.',
        retry:       'Try again',
        capped:      'The newest {count} events are shown.',
        offline_for: 'offline for {minutes} min',
        kind: {
            boot:         'Device boot',
            app_start:    'App start',
            page_load:    'Screen load',
            cec:          'HDMI-CEC',
            connectivity: 'Connection',
            tms:          'TMS',
        },
        reason: {
            boot: {
                power_off:     'Switched off with the remote or button',
                power_loss:    'Power loss (cold boot)',
                app_reboot:    'Restarted from the console',
                adb:           'Restarted over ADB',
                system_update: 'System update',
                crash:         'System crash (watchdog, kernel panic)',
                reboot:        'Restart',
                unknown:       'Unknown',
            },
            app_start: {
                update:  'App updated',
                boot:    'At device boot',
                crash:   'Restart after a crash',
                home:    'Home key',
                manual:  'Started by hand (technician)',
                unknown: 'Unknown',
            },
            page_load: {
                first_load:     'First load',
                deploy:         'New screen version',
                console_reload: 'Reloaded from the console',
                error_recovery: 'After an error',
                network:        'Network came back',
                technician:     'Reloaded by the technician',
                renderer:       'After a browser crash',
                app_start:      'At app start',
                reload:         'Reload',
            },
            connectivity: {
                offline: 'Went offline',
                online:  'Back online',
            },
            tms: {
                mac_auto:    'MAC from the TMS (automatic)',
                mac_console: 'MAC from the TMS (chosen in the console)',
            },
        },
    },
    guest_reset: {
        title:                  'Reset for the next guest',
        subtitle:               'The TV forgets the previous guest: YouTube, Netflix, Disney+, HBO Max and Prime Video sign-out, alarm, language and volume.',
        help_manual:            'Without a PMS: at reception, mark "Departs today" in the list below — at the check-out time the TVs of those rooms reset themselves. You can also reset a room or a device with its button at any time.',
        help_connector:         'With a PMS connector the screens reset themselves at check-out and when a new guest arrives. The button and the automatic reset at check-out time are for exceptions (e.g. a room change).',
        auto:                   'Automatically at check-out time',
        auto_help:              'Rooms marked "Departs today" are reset at the chosen time.',
        at:                     'Check-out time',
        at_help:                'The hotel\'s local time. At this time on the departure day the guest\'s phone link (the QR code on the TV) also ends — always, even without the automatic reset. A room marked after this time is reset at once.',
        expire_unbound:         'Links without a stay end every day',
        expire_unbound_help:    'For houses without a PMS and without stays in Occupancy: the phone link also ends at the check-out time when the room has no stay. A guest staying several nights scans the QR code on the TV again every day.',
        at_rule:                'Enter a time as HH:MM.',
        volume:                 'Volume after a reset',
        volume_help:            'The volume in percent the TV sets (where the device allows it).',
        volume_set:             'Set',
        volume_keep:            'Leave as it is',
        max_volume:             'Maximum volume',
        max_volume_help:        'Guests cannot turn the TV up beyond this percentage — neither from the phone nor with the room remote (where the device allows it).',
        max_volume_set:         'Limit',
        max_volume_none:        'No limit',
        volume_over_max:        'The volume after a reset cannot be above the maximum volume.',
        admin_only:             'An admin can change these settings.',
        column:                 'Reset',
        departure:              'Departure',
        departs_today:          'Departs today',
        action:                 'Reset for the next guest',
        cancel:                 'Cancel the reset',
        pending:                'Waiting for the screen',
        pending_since:          'Requested {at}',
        never:                  'Never',
        last:                   'Reset {ago}',
        youtube_cleared:        'YouTube cleared',
        netflix_cleared:        'Netflix cleared',
        disney_plus_cleared:    'Disney+ cleared',
        hbo_max_cleared:        'HBO Max cleared',
        prime_video_cleared:    'Prime Video cleared',
        reason: {
            console: 'from the console',
            auto:    'at check-out',
            stay:    'on a new guest',
        },
        confirm_title:          'Reset {name} for the next guest?',
        confirm_device_youtube: 'The TV signs the Google account out of YouTube (history and linked phones are deleted) and the accounts out of Netflix, Disney+, HBO Max and Prime Video, forgets screen-mirroring pairings, clears the alarm, stops playback and sets the language and volume back to the house defaults.',
        confirm_device_screen:  'This device cannot clear apps, so only the room screen is reset: alarm, playback, language and volume. YouTube, Netflix, Disney+, HBO Max and Prime Video stay signed in.',
        confirm_room:           'Every TV in the room is reset: YouTube, Netflix, Disney+, HBO Max and Prime Video sign-out (where the device can), alarm, playback, language and volume.',
        confirm_when:           'A TV that is on does it within a minute, one that is off when it is switched on.',
        requested:              'Reset requested — the screen runs it within a minute.',
        cancelled:              'The reset is cancelled.',
    },
    provisioning: {
        other_tenant: 'An installation is still running in another house. The list and the report below are its own until it ends.',
        agent_error: {
            unreachable:   'The agent on the laptop does not answer.',
            timeout:       'The agent on the laptop did not answer in time.',
            session:       'The agent no longer knows this connection (was it restarted?). Type a new code.',
            wrong_code:    'Wrong code. Type the six digits the agent printed.',
            locked:        'Too many wrong codes. Restart the agent and type the new code.',
            failed:        'The agent refused.',
            failed_reason: 'Agent: {reason}',
            wrong_code_left: 'Wrong code. Attempts left: {n}.',
            origin:        'The agent does not accept this page. Start it for this console\'s address (--origin).',
            no_adb:        'The agent cannot find adb.',
            adb_failed:    'adb failed ({step}): {output}',
            not_connected: 'The box is not connected in adb ({state}).',
            no_apk:        'The console has no app of this variant — upload the APK on the provider\'s page.',
            download:      'The agent could not download the app from the console.',
            mismatch:      'The downloaded app does not match the release (size or sha256).',
        },
        agent: {
            title:           '1. The agent on the laptop',
            subtitle:        'tv-provision-agent on a laptop in the hotel network reaches the boxes by ADB.',
            connected:       'Connected',
            check:           'Check again',
            not_running:     'The agent is not running or the browser cannot reach it.',
            timeout:         'The agent did not answer in time.',
            watching:        'The page looks for it again every few seconds — start it and it connects by itself.',
            help_start:      'Start tv-provision-agent on the laptop and leave it open; it prints a six-digit code.',
            help_permission: 'Chrome may ask whether the page may reach devices on your local network — choose Allow.',
            help_denied:     'If you blocked it, allow it in the site settings (the icon left of the address) and reload the page.',
            help_browser:    'Use Chrome or Edge on the same laptop the agent runs on; Safari does not let an https page reach the laptop.',
            address:         'Agent address: {address}',
            version:         'Agent version',
            no_adb:          'adb not found',
            adb_key_own:     'The agent uses its own ADB key: every box asks "Allow USB debugging" on the first connect — the TMS confirms it with the keys (Settings › TMS).',
            no_adb_help:     'Without adb the agent cannot reach the boxes. Install Android platform-tools or start the agent with --adb <path>.',
            code:            'Agent code',
            code_help:       'The six digits the agent printed at start. Valid for this browser tab.',
            code_rule:       'The code has six digits.',
            pair:            'Connect',
            paired:          'The agent is connected.',
            paired_help:     'The agent is connected to this tab.',
            forget:          'Disconnect',
        },
        boxes: {
            title:           '2. Boxes from the TMS',
            subtitle:        'Give each box to install a room, a name and a usage — in the table or by importing a CSV.',
            import:          'Import CSV',
            truncated:       'The TMS returned more boxes than the list shows; the first ones are shown.',
            search:          'Search MAC, IP, room …',
            only_selected:   'Selected only',
            summary:         '{selected} selected · {reachable} of {total} reachable',
            select_all:      'Select all reachable',
            select:          'Select {mac}',
            box:             'Box',
            device:          'Existing device',
            no_lan_ip_short: 'no LAN IP',
            replace:         'Replace',
            room_pick:       'Choose a room',
            empty:           'The TMS knows no box of this house.',
            offline:         'The box is off line.',
            no_lan_ip:       'The TMS does not know the box\'s LAN address.',
            needs_replace:   'A device has this MAC already — tick Replace.',
            needs_room:      'Choose a room.',
            needs_name:      'Type a name (up to 60 characters).',
            load_failed:     'The boxes could not be loaded from the TMS.',
        },
        csv: {
            title:       'Import boxes (CSV)',
            description: 'One line per box: mac,room,name. The room by its name in any language; the name is optional.',
            drop:        'Drop a CSV file here or choose one',
            drop_help:   'CSV or text separated by commas, semicolons or tabs.',
            text:        'Or paste the lines',
            text_help:   'The first line may be a header (mac,room,name). Without a name a box is named after its room ("Room 101 — TV").',
            apply:       'Import',
            applied:     '{n} line imported | {n} lines imported',
            taken:       '{n} line imported | {n} lines imported',
            skipped_n:   '{n} skipped | {n} skipped',
            skipped:     'Lines skipped',
            line:        'Line {n}',
            problem: {
                columns:        'too few columns',
                mac:            'not a MAC address',
                unknown_mac:    'the TMS does not know this MAC',
                duplicate:      'MAC repeated',
                room:           'no such room',
                room_ambiguous: 'several rooms of that name',
            },
        },
        developer: {
            title:       '3. Developer in the TMS',
            subtitle:    'ADB on a box answers only once "Enable Developer" is on in the TMS.',
            help:        'The TMS Open API has no such switch: in the TMS web UI (manage.atvbridge.com) find the selected boxes by MAC and switch Developer on for them. Then tick below.',
            none:        'Select the boxes in step 2 first.',
            list:        'MAC addresses of the selected boxes',
            copy_commas: 'Copy {n} MAC comma-separated | Copy {n} MACs comma-separated',
            copy_lines:  'Copy {n} MAC one per line | Copy {n} MACs one per line',
            open_tms:    'Open the TMS',
            done:        'Developer is on',
            done_help:   'For all the selected boxes, in the TMS.',
            copied:      'The MAC addresses are copied.',
            copy_failed: 'Copying failed – select the list and copy it yourself.',
        },
        run: {
            title:                '4. Installation',
            subtitle:             'Each box step by step: ADB, the app, the token, the launch, the settings, pairing, the TMS group.',
            concurrency:          'At once',
            concurrency_help:     'How many boxes at a time (1–5).',
            start:                'Start installation ({n})',
            stop:                 'Stop',
            stopping:             'Stopping …',
            retry:                'Retry',
            left_out:             '{n} selected box lacks something (room, name, Replace) and will not be installed. | {n} selected boxes lack something (room, name, Replace) and will not be installed.',
            need_agent:           'Start the agent on the laptop (step 1).',
            need_adb:             'The agent has no adb (step 1).',
            need_pairing:         'Connect the agent with its code (step 1).',
            need_boxes:           'Select boxes with a room and a name (step 2).',
            need_developer:       'Switch Developer on in the TMS and tick it (step 3).',
            seconds:              '{s} s',
            minutes:              '{m} min {s} s',
            replace_report_title: 'Start a new installation?',
            replace_report:       'The report of the last installation on this page is replaced by the new one. Download it first if you need it.',
            group_set:            'group {group}',
            group_none:           'no TMS group set',
            group_line:           'TMS group: {group} · ADB keys: {keys}',
            group_line_none:      'No TMS group set (the Group step is skipped) · ADB keys: {keys}',
        },
        run_error: {
            unreachable:  'The box does not answer ADB — is Developer on in the TMS and the laptop on the same network?',
            unauthorized: 'The box did not allow ADB ("Allow USB debugging"). Check the keys in Settings › TMS or confirm on the TV.',
            allowed:      'ADB allowed with the TMS remote',
            pair_timeout: 'The box did not pair within 90 s.',
            expired:      'The token expired.',
            no_token:     'No token.',
            failed:       'The step failed.',
            keys:         'The TMS did not press the keys.',
            mac_taken:    'The device »{name}« has this MAC already — tick Replace.',
            token:        'The installation token could not be created.',
            poll:         'The pairing status could not be read.',
            group:        'The TMS did not add the box to the group.',
            group_unknown: 'The TMS has no group »{group}« — fix the name in Settings › TMS.',
            rate_limited: 'Too many TMS requests. Wait a few minutes and retry.',
        },
        step: {
            connect:  'ADB',
            install:  'App',
            token:    'Token',
            launch:   'Launch',
            settings: 'Settings',
            paired:   'Paired',
            group:    'Group',
        },
        step_status: {
            pending: 'waiting',
            running: 'running',
            ok:      'done',
            failed:  'failed',
            skipped: 'skipped',
        },
        box_status: {
            idle:    'not started',
            queued:  'queued',
            running: 'running',
            done:    'installed',
            failed:  'failed',
            stopped: 'stopped',
        },
        report: {
            title:     '5. Report',
            download:  'Download CSV',
            retry_all: 'Retry failed ({n})',
        },
    },
    tms: {
        title:                'TMS (ATV Bridge)',
        subtitle:             'TG MAXX boxes (Translite Global): at a reset for the next guest the TMS clears the apps where our app cannot.',
        help:                 'On TG MAXX boxes our app cannot sign the guest out of Netflix, YouTube, Disney+, HBO Max and Prime Video. The »Clear App Data« task in the ATV Bridge TMS (manage.atvbridge.com) can: set it up there once — the list of apps whose data is kept (io.imprion.tv among them), everything else is cleared; optionally also removing the Google account and the apps the guest installed. Enter its name here.',
        enabled:              'On',
        enabled_help:         'At a reset for the next guest (the button, automatically at check-out, a new guest) the TMS gets the task for that box.',
        base_url:             'Open API address',
        base_url_help:        'The base address of your TMS’s Open API (https), e.g. https://manage.atvbridge.com/openApi — Translite confirms the exact path.',
        api_key:              'API key',
        api_key_help_set:     'A key is stored and never shown. Leave empty to keep it.',
        api_key_help_new:     'The Open API key from the TMS.',
        clear_task:           '»Clear App Data« task name',
        clear_task_help:      'The task’s exact name as the TMS has it.',
        mode:                 'When to clear through the TMS',
        mode_help:            'Boxes with our own app reset (Device Owner) clear the apps themselves.',
        mode_fallback:        'Only where our app cannot (recommended)',
        mode_always:          'At every reset',
        mode_never:           'Never automatically (only with the device’s button)',
        test:                 'Test connection',
        test_mac:             'A device’s MAC to test (optional)',
        test_ok:              'The connection works — TMS {version}.',
        test_failed:          'The connection does not work: {error}',
        test_terminal:        'Device {mac}: {state}',
        test_unsaved:         'The test uses what is typed, also unsaved.',
        admin_only:           'An administrator can change the settings.',
        terminal_unknown:     'The TMS does not know this device (MAC {mac}).',
        online:               'online',
        offline:              'offline',
        software:             'Software',
        model:                'Model',
        group:                'Group',
        public_ip:            'Public IP',
        last_contact:         'Last contact',
        serial:               'Serial number',
        refresh:              'Refresh',
        reboot:               'Reboot (TMS)',
        clear_apps:           'Clear apps (TMS)',
        confirm_reboot_title: 'Reboot {name} through the TMS?',
        confirm_reboot:       'The TMS reboots the box. The TV has no picture for a minute or two.',
        confirm_clear_title:  'Clear the apps on {name} through the TMS?',
        confirm_clear:        'The TMS runs the »Clear App Data« task on the box: the data of every app but those chosen in the TMS is erased (signing out of Netflix, YouTube, Disney+, HBO Max and Prime Video). Do not do it while the guest is still in the room.',
        rebooted:             'The TMS accepted the reboot.',
        cleared:              'The TMS accepted clearing the apps.',
        last:                 'Last through the TMS',
        manual:               'with the button',
        find:                 'Find in the TMS',
        replace:              'Replace',
        no_mac:               'The device has no MAC address, so the TMS cannot recognise it. Find it among the boxes in the TMS.',
        no_mac_reception:     'The device has no MAC address, so the TMS cannot recognise it. An administrator can find it in the TMS.',
        pick_title:           'The box in the TMS for {name}',
        pick_description:     'Boxes from the TMS no device of this house has yet — the most likely first.',
        pick_summary:         'Boxes in the TMS: {terminals} · already assigned to other devices: {assigned}',
        pick_truncated:       'Only the first 2000 boxes from the TMS are shown.',
        pick_empty:           'There is no free box in the TMS — all of them belong to other devices.',
        pick_choose:          'Choose',
        pick_lan_ip:          'LAN IP',
        pick_confirm_title:   'Give {name} the MAC {mac}?',
        pick_confirm:         'The device gets the MAC of the box from the TMS ({model}). Through it the TMS clears and reboots it at a reset. You can replace it at any time.',
        pick_confirm_replace: 'The device now has the MAC {old}; the MAC of the box from the TMS ({model}) replaces it.',
        pick_use:             'Assign MAC',
        picked:               'MAC {mac} saved.',
        pick_taken:           'The device »{name}« already has this MAC.',
        pick_unknown:         'The TMS no longer knows this MAC address. Refresh the list.',
        group_name:           'Group for Installation',
        group_help:           'The name of the TMS group Installation adds the boxes to. Empty = no group.',
        adb_keys:             'Keys for "Allow USB debugging"',
        adb_keys_help:        'The remote keys the TMS presses during Installation when a box asks to allow ADB — in order. Default right, OK.',
        adb_key_remove:       'Remove {key}',
        adb_keys_empty:       'No keys.',
        adb_keys_default:     'Default (OK, down, left, OK)',
        adb_keys_required:    'At least one key.',
        key: {
            home:  'Home',
            back:  'Back',
            up:    'Up',
            down:  'Down',
            left:  'Left',
            right: 'Right',
            ok:    'OK',
        },
        match: {
            lan_ip:    'same LAN IP',
            serial:    'same serial number',
            public_ip: 'same public IP',
            online:    'online',
        },
        outcome: {
            pending: 'TMS: waiting',
            pushed:  'TMS: sent',
            failed:  'TMS: failed',
            skipped: 'TMS: skipped',
        },
        skip_reason: {
            no_mac:    'the device has no MAC address',
            own_reset: 'our app clears them itself',
            platform:  'a TV set',
            never:     'automatic clearing off',
        },
    },
    tv: {
        title:          'TV',
        subtitle:       'Channels, categories, the TV guide and the look of the list on the room screens.',
        tab_channels:   'Channels',
        tab_categories: 'Categories',
        tab_epg:        'EPG',
        tab_themes:     'Look',
        tab_settings:   'TV settings',
    },
    channel: {
        channels:           'Channels',
        channel:            'Channel',
        subtitle:           '{n} channels, {enabled} enabled. Drag or move with the arrows.',
        add:                'New channel',
        edit:               'Edit channel',
        delete:             'Delete channel',
        name:               'Name',
        note:               'Note',
        source:             'Source',
        source_help:        'The part after the stream base address (usually the channel’s ID) or a full http(s):// address.',
        source_placeholder: '12',
        source_mediaklikk:  'web (MTVA)',
        mediaklikk_help:    'Hungarian public TV from the web: mediaklikk:<id> — M1 mtv1live, M2 mtv2live, M4 Sport mtv4live, M5 mtv5live, Duna dunalive, Duna World dunaworldlive. Always played through the server.',
        mediaklikk_pick:    'MTVA:',
        mediaklikk_invalid: 'After mediaklikk: comes the channel’s ID: 3–30 lower-case letters and digits (mtv1live).',
        resolved:           'Address: {url}',
        type:               'Stream type',
        type_help:          'Automatic is right for almost everything; HLS or MPEG-TS only when the screen does not recognise it.',
        categories:         'Categories',
        no_categories:      'No categories',
        all_categories:     'All categories',
        logo:               'Logo',
        enabled:            'Enabled',
        enabled_help:       'A disabled channel is not shown on the screens.',
        adult:              'Adult',
        adult_help:         'How it is shown is set in TV settings.',
        adult_badge:        '18+',
        epg:                'EPG',
        search:             'Search channels …',
        only_without_epg:   'Only without EPG',
        signal:             'Signal',
        check_now:          'Check now',
        only_without_signal: 'Only channels without signal',
        check_ok:           'Channel {name} is working.',
        check_down:         'Channel {name} is down: {reason}.',
        check_unknown:      'Channel {name} cannot be checked.',
        order_filtered:     'The order can be changed with the filter off.',
        empty:              'There are no channels yet.',
        empty_help:         'Add channels by hand or import them from the old system.',
        confirm_delete:     'Delete channel {name}? Its EPG mapping goes with it.',
        languages:          'Languages',
        languages_help:     'The language the channel broadcasts in — usually one. A guest whose TV is in that language sees the channel among the first and in that language’s category.',
        languages_max:      'At most {n} languages.',
        no_languages:       'No language set',
        all_languages:      'All languages',
        no_language:        'No language',
    },
    stream: {
        button:           'Stream source',
        title:            'Stream source',
        help:             'One base address and one token for all of the hotel’s channels — a channel is only the ID at the end.',
        base:             'Base address',
        base_help:        '{\'{\'}token{\'}\'} in the address is replaced with the token. Empty = every channel has a full address.',
        base_placeholder: 'http://avis2.ittim-online.net/stream/hotel/{\'{\'}token{\'}\'}/',
        base_rule:        'The address starts with http:// or https://.',
        no_base:          'No base address — channels have full addresses.',
        token:            'Token',
        token_set:        'A token is stored. Empty keeps it.',
        token_unset:      'No token is set.',
        token_clear:      'Remove',
        token_keep:       'Keep',
        proxy:            'Play through the server',
        change:           'Change on the Channels tab',
    },
    category: {
        categories:     'Categories',
        subtitle:       'The screen’s groups of channels, named in every language, in their own order.',
        language_note:  'After these the screen adds categories by the channels’ languages (Deutsch, English …) by itself — switch them on or off in TV settings.',
        add:            'New category',
        edit:           'Edit category',
        delete:         'Delete category',
        name:           'Name',
        name_help:      'The name in each of the hotel’s languages.',
        translated:     'Translated',
        not_translated: 'Translation missing',
        channel_count:  '{n} channel | {n} channels',
        empty:          'There are no categories yet.',
        empty_help:     'Categories group channels into bands, e.g. “Slovenian”, “News”.',
        confirm_delete: 'Delete category {name}? It is removed from its channels ({n}).',
    },
    epg: {
        providers:               'EPG providers',
        providers_subtitle:      'XMLTV sources the server downloads regularly.',
        providers_empty:         'There is no provider yet.',
        provider:                'Provider',
        provider_add:            'New provider',
        provider_edit:           'Edit provider',
        provider_name:           'Name',
        url:                     'XMLTV address',
        url_help:                'An XMLTV file over http(s); .gz is accepted.',
        language:                'Guide language',
        language_help:           'The language the programme titles are in. The room screen shows the guide of the provider in the guest\'s language.',
        update_minutes:          'Refresh every (min)',
        update_minutes_help:     '15–1440 minutes.',
        update_minutes_range:    'Between 15 and 1440.',
        enabled:                 'Enabled',
        enabled_help:            'A disabled provider is not refreshed.',
        disabled:                'Disabled',
        test:                    'Test the source',
        test_help:               'Downloads the file before saving and checks that it is XMLTV.',
        last_update:             'Last refresh',
        never:                   'Never',
        every:                   'every {n} min',
        channels:                'Channels',
        counts:                  '{channels} in the source · {mappings} mapped',
        refresh:                 'Refresh now',
        refreshed:               '{name} is refreshed.',
        refresh_failed:          'Refreshing failed.',
        confirm_delete_provider: 'Delete provider {name}?',
        mappings:                'Channel mapping',
        mappings_subtitle:       'Which provider channels feed our channel — at most one per provider. Without EPG: {n}.',
        suggest:                 'Suggest',
        suggest_title:           'Mapping suggestions',
        suggest_help:            'For every channel without EPG the best channel of all providers by name. Accept the ticked ones in one go.',
        suggest_help_provider:   'For every channel this provider does not feed yet, its best channel by name. Accept the ticked ones in one go.',
        suggest_none:            'No suggestions — every channel is mapped for this provider or no names match.',
        suggest_from:            'Suggest from {name}',
        suggest_all:             'All ({n})',
        suggest_selected:        'selected: {n}',
        suggest_accept:          'Accept ({n})',
        suggest_done:            'Channels mapped: {n}.',
        all_providers:           'All providers (channels without EPG)',
        all_providers_used:      'The channel is mapped for every provider already.',
        sources:                 'Provider channels',
        entries:                 'Entries',
        entry_count:             '{n} entry | {n} entries',
        max_days:                '{n} days ahead',
        max_days_label:          'Days ahead',
        max_days_help:           '0 = everything the source has; else 1–30.',
        max_days_help_short:     '0 = all',
        not_mapped:              'Not mapped',
        mapping_unknown:         'Not in the source',
        channel_disabled:        'disabled',
        map:                     'Map',
        map_another:             'Add a provider',
        unmap:                   'Remove mapping',
        confirm_unmap:           'Remove the mapping of channel {name} from provider {provider}? Its stored EPG entries are deleted.',
        map_title:               'EPG for {name}',
        map_help:                'Choose the provider and its channel. The search ignores case, diacritics and HD tags.',
        search_provider_channel: 'Search provider channels …',
        no_provider_channels:    'No matches.',
        used_elsewhere:          'already used',
        already_mapped:          'This channel already has an EPG mapping for this provider.',
        entries_title:           'Programme: {name} · {provider}',
        entries_help:            'Stored EPG entries by date (at most 7 days at a time).',
        entries_empty:           'No entries in this period.',
        from:                    'From',
        to:                      'To',
        reload:                  'Reload',
        range_order:             'The “to” date is before “from”.',
        range_max:               'At most {n} days at a time.',
        delete_range:            'Delete period',
        confirm_delete_range:    'Delete the entries of this period ({n})? The next import brings them back while the channel is mapped.',
        deleted_entries:         'Entries deleted: {n}.',
    },
    theme: {
        saved:               'Saved themes',
        new:                 'New theme',
        edit:                'Edit theme',
        empty:               'No theme saved yet — the screens use the built-in look.',
        active:              'Active',
        active_help:         'Every room screen uses the theme “{name}”.',
        none_active:         'No theme is active — the screens use the built-in look.',
        activate:            'Use',
        activated:           'The theme “{name}” is now active.',
        deactivate:          'Use the built-in look',
        deactivated:         'The screens use the built-in look.',
        duplicate:           'Duplicate',
        copy_of:             '{name} (copy)',
        default_name:        'Theme {n}',
        revert:              'Discard changes',
        saved_inactive:      'The theme is saved (not active yet).',
        confirm_delete:      'Delete the theme “{name}”?',
        name:                'Name',
        background:          'Background',
        kind_solid:          'Solid',
        kind_gradient:       'Gradient',
        color:               'Colour',
        angle:               'Direction',
        stops:               'Gradient colours ({n})',
        stops_help:          '2–5 colours; position 0–100 %.',
        stops_rule:          'A gradient needs 2–5 colours with positions 0–100 in order.',
        stop_at:             'Position %',
        stop_add:            'Add colour',
        stop_remove:         'Remove colour',
        opacity:             'Opacity',
        card:                'Channel card',
        card_background:     'Card background',
        card_text:           'Text',
        card_text_secondary: 'Secondary text',
        focus_background:    'Focused card',
        focus_text:          'Focused text',
        progress_divider:    'Progress and divider',
        progress_track:      'Progress track',
        progress_fill:       'EPG progress',
        divider:             'Divider',
        preview:             'Preview',
        preview_next:        'Next: Weather · Sport · Film',
        pick_color:          'Pick a colour',
        hex_rule:            'A colour as #RRGGBB.',
    },
    tv_settings: {
        title:      'TV settings',
        subtitle:   'How the room screens offer channels.',
        adult:      'Adult channels',
        adult_help: 'Applies to channels marked “adult”.',
        channel_order:      'Channel order',
        channel_order_help: 'By the channels’ languages (Channels › Languages).',
        language_categories:      'Language categories',
        language_categories_help: 'Adds a category for every language with at least two channels (Deutsch, English …) after your own — automatically, from the channels’ languages; the guest’s language is always among them, and on top when it is not the house’s default. Adult channels behind the PIN are not in them.',
    },
    channel_order: {
        guest_language:      'Guest’s language first',
        guest_language_help: 'A guest whose TV is in German sees the German channels first, then all the others in your order (default).',
        house:               'House order',
        house_help:          'Every guest sees the channels in your order.',
    },
    content: {
        title:        'Content',
        subtitle:     'What the guest sees on the room screen.',
        tab_general:  'General',
        tab_welcome:  'Welcome',
        tab_home:     'Home screen',
        tab_hotel:    'Hotel',
        tab_services: 'Services',
        tab_music:    'Music',
        tab_program:  'Programme',
        tab_signage:  'Signage',
        tab_invitations: 'Invitations',
        tab_themes:   'Themes',
        tab_seasonal: 'Seasonal themes',
        tab_room_guide: 'Your room',
    },
    room_guide: {
        housekeeping:           'The "Something missing?" button',
        housekeeping_help:      'On the TV and the phone the guest reports what is missing or does not work; the request arrives in Guest requests (Housekeeping).',
        reception:              'Requests to the reception',
        reception_help:         'Through Milo the guest asks for a late check-out, an extra night, a taxi or a restaurant table; the request arrives in Guest requests (Reception). The PMS is not changed.',
        on:                     'On',
        off:                    'Off',
        scope:                  'For which rooms',
        scope_search:           'Search a type or a room …',
        scope_house:            'All rooms',
        scope_house_help:       'Applies to every room. Room types and single rooms inherit it and can add to it.',
        scope_type_help:        'Applies to the rooms of type {name} ({n}): inherits "All rooms"; customize, hide or add entries.',
        scope_room_help:        'Applies to this room only: inherits "All rooms"; customize, hide or add entries.',
        scope_room_type_help:   'Applies to this room only: inherits "All rooms" and the type {type}; customize, hide or add entries.',
        additions_room:         'This room only',
        additions_type:         'This type only',
        inherited:              'Inherited',
        customize:              'Customize',
        customized:             'Customized',
        hide:                   'Hide',
        hidden_here:            'Hidden',
        show_again:             'Show',
        reset:                  'Reset',
        apply:                  'Apply',
        untitled:               'Untitled',
        icon:                   'Icon',
        icon_change:            'Change',
        photo:                  'Photo',
        photo_help:             'Optional; instead of the icon.',
        where_title:            'Where to find it',
        where_subtitle:         'E.g. "Hair dryer — in the drawer under the bathroom mirror" ({n} of at most {max})',
        where_add:              'Add a thing',
        where_edit:             'Edit thing',
        where_empty:            'Nothing yet.',
        where_name:             'What',
        where_name_help:        'E.g. "Hair dryer", "Extra blanket", "Safe".',
        where_text:             'Where it is',
        where_text_help:        'E.g. "In the drawer under the bathroom mirror".',
        howto_title_section:    'How it works',
        howto_subtitle:         'Short step-by-step how-tos: air conditioning, safe, blinds, TV, coffee machine … ({n} of at most {max})',
        howto_add:              'Add a how-to',
        howto_edit:             'Edit how-to',
        howto_empty:            'No how-to yet.',
        howto_title:            'Title',
        howto_title_help:       'E.g. "Air conditioning" or "Safe".',
        steps:                  'Steps ({n} of at most {max})',
        steps_n:                '{n} step | {n} steps',
        steps_required:         'Write at least one step.',
        step_placeholder:       'E.g. "Press ON on the remote on the bedside table."',
        step_add:               'Add a step',
        step_remove:            'Remove step',
        minibar_title:          'Minibar',
        minibar_subtitle:       'What is in the minibar and at what price — charged to the room ({n} of at most {max})',
        minibar_note:           'Note under the minibar',
        minibar_note_default:   'Charged to the room',
        minibar_note_help:      'Empty = "Charged to the room".',
        minibar_note_help_scope: 'Empty = the inherited note (grey).',
        minibar_add:            'Add an item',
        minibar_edit:           'Edit item',
        minibar_empty:          'The minibar is empty.',
        minibar_from_menu:      'From the POS menu',
        minibar_manual:         'Manual',
        minibar_item:           'Item',
        minibar_item_help:      'Any item of the imported menu, also one not offered for room service; the price follows the menu.',
        minibar_item_pick:      'Pick an item …',
        minibar_item_required:  'Pick an item.',
        minibar_no_menu:        'No menu imported yet (Room service) — type the item by hand.',
        minibar_missing:        'The item is gone from the menu or has no price — guests do not see it.',
        minibar_missing_short:  'Not on the menu',
        minibar_price_from_menu: 'Price from the menu',
        minibar_name_override:  'Name for the guest',
        minibar_name_override_help: 'Empty = the item\'s name on the menu.',
        minibar_name:           'Name',
    },
    general_settings: {
        languages:        'Languages',
        languages_help:   'The languages the content is written in and the guest can choose on the screen.',
        languages_keep:   'A removed language keeps its texts — they come back when it is switched on again.',
        default_language: 'Default language',
        location:         'Weather town',
        location_help:    'The screens’ weather forecast is for this town.',
    },
    appearance: {
        title:             'Screen look',
        subtitle:          'The room screen’s name, colours, logo and style.',
        display_name:      'Name on screen',
        display_name_help: 'Empty = the customer’s name.',
        tagline:           'Tagline',
        monogram:          'Monogram',
        monogram_help:     '1–2 letters while there is no logo.',
        primary:           'Primary colour',
        accent:            'Accent',
        color_help:        'Empty = the product’s colours.',
        reset_color:       'Default colour',
        theme:             'Theme',
        typeface:          'Typeface',
        radius:            'Corners',
        logo:              'Logo',
    },
    welcome: {
        title:               'Welcome',
        subtitle:            'The greeting on the first screen, in each of the hotel’s languages.',
        greeting:            'Greeting for the guest',
        greeting_help:       '{\'{\'}name{\'}\'} = the guest’s first name (from the PMS), {\'{\'}room{\'}\'} = the room’s name.',
        empty_greeting:      'Greeting without a guest',
        empty_greeting_help: 'When nobody is in the room or the hotel has no PMS; without {\'{\'}name{\'}\'}.',
        subtitle_field:      'Subtitle',
        show_departure:      'Show departure',
        show_departure_help: 'The guest’s departure date (from the PMS).',
        show_room_number:    'Show the room',
        show_weather:        'Show the weather',
        unknown_variable:    'Only {\'{\'}name{\'}\'} and {\'{\'}room{\'}\'} are allowed.',
        no_name_in_empty:    'The greeting without a guest must not contain {\'{\'}name{\'}\'}.',
        preview:             'Preview',
        preview_language:    'Preview language',
        preview_guest:       'Guest in the room',
        sample_name:         'Guest name',
        sample_room:         'Room',
        preview_room:        'Room {room}',
        preview_departure:   'Departure: Sunday',
        preview_weather:     '☀ 18 °C',
    },
    home: {
        title:         'Home screen',
        subtitle:      'The tiles on the home screen: which are shown, their title, background and order.',
        shown:         'Shown',
        hidden:        'Hidden',
        tile_title:    'Title (empty = default)',
        tile_subtitle: 'Subtitle (empty = default)',
        background:    'Background',
        features_help: 'The screen leaves out tiles it cannot fill without a PMS link (e.g. the bill).',
        size:          'Size',
        size_help:     'A tile\'s size (Automatic, 1×1, 2×1, 1×2, 2×2): once at least one tile has one, the home screen is a grid and the tiles are placed in order — the preview is under the list.',
        home_greeting: 'Home is not a tile — the greeting at the top of the TV and the phone is edited in ',
        home_greeting_link: 'Content › Welcome',
        merged_note: 'This tile is drawn inside another screen right now (e.g. the letter inside “Your stay”). Its title, subtitle and background apply once it has its own tile again.',
        cast_help:     'Apps shows only on TVs with a receiver for a phone (Android with Imprion TV 1.2.0 or newer).',
        edit_in:       'Edit: {place}',
        edit_place: {
            home: 'Content › Welcome',
            letter: 'Content › Hotel › Welcome letter',
            house: 'Content › Hotel › House',
            local: 'Content › Hotel › Surroundings',
            booking: 'Content › Hotel › Bookings and weather',
            program: 'Content › Programme',
            services: 'Content › Services',
            music: 'Content › Music',
            invitations: 'Content › Invitations',
            room_guide: 'Content › Your room',
            alarm: 'Content › Themes',
            tv: 'TV › Channels',
            room_service: 'Room service',
            requests: 'Guest requests',
            bill: 'PMS connection',
            nearby: 'PMS connection',
            milo: 'Milo › Settings',
            feedback: 'Praise, complaints and suggestions › Settings',
        },
    },
    hotel: {
        help:                'House, welcome letter, area, bookings and weather. Revision {revision}.',
        house:               'House',
        house_help:          'Address, Wi-Fi, opening hours and phone numbers.',
        address:             'Address',
        wifi_network:        'Wi-Fi network',
        wifi_password:       'Wi-Fi password',
        reception_key:       'Reception key',
        reception_key_help:  'The number on the room phone.',
        photo:               'Photo',
        emergency:           'Emergency',
        hours:               'Opening hours ({n}/{max})',
        hours_label:         'What (e.g. Breakfast)',
        hours_value:         'When (e.g. 7.00–10.00)',
        hours_add:           'Add a row',
        phone_keys:          'Phone keys ({n}/{max})',
        phone_key:           'Key',
        phone_label:         'Who (e.g. Restaurant)',
        phone_add:           'Add a key',
        letter:              'Welcome letter',
        letter_help:         'Introduction, directions with icons and signature.',
        intro:               'Introduction',
        directions:          'Directions ({n}/{max})',
        direction_add:       'Add a direction',
        signature:           'Signature',
        local:               'Area',
        local_help:          'Events and places nearby.',
        qr:                  'QR code for the map',
        qr_help:             'Entries with a map address get a QR code on the screen.',
        events:              'Events ({n}/{max})',
        event_name:          'Event',
        event_date:          'Date',
        event_time:          'Time',
        event_venue:         'Venue',
        event_add:           'Add an event',
        places:              'Places ({n}/{max})',
        place_name:          'Name',
        place_kind:          'Kind',
        place_hours:         'Open',
        place_add:           'Add a place',
        distance:            'Distance',
        price:               'Price',
        discount:            'Discount',
        note:                'Note',
        maps_url:            'Map address',
        photo_credit:        'Photo author and licence',
        photo_credit_help:   'Shown with the photo (e.g. photos from Wikimedia Commons).',
        event_source_url:    'Source (the event\'s web page)',
        event_description:   'Description',
        event_description_help: 'The longer text of the detail on the TV and the phone (up to 600 characters).',
        photo_focus:         'Picture — focus point',
        photo_focus_help:    'Click the part of the picture that must stay visible. The TV and the phone crop around this point; the detail always shows the whole picture. The arrow keys move the point too.',
        photo_focus_value:   'Focus point: {x} % from the left, {y} % from the top',
        photo_focus_reset:   'Reset to centre',
        photo_poster:        'This is a poster (lots of text) — show the whole poster on the card, do not crop it',
        preview_tv:          'Card on the TV',
        preview_phone:       'Card on the phone',
        preview_sample:      'Event name',
        booking:             'Bookings',
        booking_help:        'Booking services on the screen (only with a PMS that supports it).',
        booking_enabled:     'Allow bookings',
        lead_hours:          'Hours ahead at least',
        lead_hours_help:     'How many hours before the slot it can still be booked.',
        booking_note:        'Note',
        weather:             'Weather',
        weather_help:        'The forecast for the hotel’s town (General).',
        weather_enabled:     'Show the weather',
        weather_days:        'Forecast days',
        weather_hourly:      'By the hour too',
        weather_preview:     'Test the forecast',
        program_source:      'Programme of the day',
        program_source_help: 'Where the screen takes the programme of the day from.',
        tab_house:           'House',
        tab_hours:           'Hours and phones',
        tab_letter:          'Welcome letter',
        tab_events:          'Area: events',
        tab_places:          'Area: places',
        tab_more:            'Bookings and weather',
        hours_help:          'Opening hours and the room phone’s keys.',
        unsaved:             'Unsaved',
        leave_unsaved:       'You have not saved the changes on the Hotel page. Leave anyway?',
        leave:               'Leave',
    },
    local_events: {
        title:          'Automatic review of the area',
        help:           'On the chosen days, look for new events nearby and suggest removing past ones. The suggestions collect here.',
        enabled:        'Turn on the automatic review',
        enabled_help_schedule: '{when} at {time} it looks for new nearby events.',
        days:           'Days',
        days_help:      'The days of the week the review runs.',
        days_none:      'Choose at least one day.',
        day_short:      { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' },
        every_first:    { mon: 'Every Monday', tue: 'Every Tuesday', wed: 'Every Wednesday', thu: 'Every Thursday', fri: 'Every Friday', sat: 'Every Saturday', sun: 'Every Sunday' },
        day_name:       { mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday', fri: 'Friday', sat: 'Saturday', sun: 'Sunday' },
        every_day:      'Every day',
        and:            'and',
        time_format:    '{h}:{m}',
        time:           'Time',
        time_help:      'The hotel’s local time.',
        run_now:        'Run now',
        running:        'Milo is reviewing the area … This can take up to three minutes.',
        run_done:       'Review finished: {added} new suggestions, {removed} past events to remove.',
        run_not_done:   'The review did not search',
        run_busy:       'A review is already running — wait for it to finish.',
        run_too_soon:   'A review has just run. You can run it again {time}.',
        run_error:      'The review could not be started.',
        next_review:    'Next review: {when}',
        suggestions_n:  '{n} suggestion | {n} suggestions',
        outcome:        { not_configured: 'no key or location set', over_budget: 'Milo’s daily budget is used up', failed: 'the search failed', interrupted: 'interrupted by a restart' },
        photo_credit:   'Photo: {credit}',
        photo_license:  'Licence',
        on_map:         'On the map',
        place_added:    'Place added to the area.',
        radius:         'Radius (km)',
        radius_help:    'How far from the hotel it searches.',
        kinds:          'Event kinds',
        kinds_help:     'Leave empty for any kind.',
        kinds_any:      'Any kind',
        not_configured: 'The review needs the provider’s Anthropic key and the hotel’s location.',
        last_review:    'Last review: {when}, {result}',
        review_button:  'Suggestions',
        review_title:   'Suggestions for the area',
        empty:          'No new suggestions.',
        source:         'Source:',
        add_action:     'Add',
        add_reject:     'Dismiss',
        remove_action:  'Remove',
        remove_keep:    'Keep',
        added:          'Event added to the area.',
        removed:        'Event removed from the area.',
        dismissed:      'Suggestion dismissed.',
        saved:          'Settings saved.',
        save_error:     'The setting could not be saved.',
        decide_error:   'The suggestion could not be processed.',
        translating:    'Milo is translating into {n} language and saving … | Milo is translating into {n} languages and saving …',
        saving:         'Milo is saving …',
        added_translated: 'Added and translated into {n} language. | Added and translated into {n} languages.',
        added_untranslated: 'Added; the translation failed',
        added_untranslated_help: 'Fill in the missing languages in Content › Hotel.',
    },
    local_places: {
        title:        'Find places',
        help:         'Milo searches the web for interesting sights, restaurants, activities, shops and services within the radius, with an openly licensed photo (Wikimedia Commons) where there is one. Places already listed or dismissed are not offered again.',
        kinds:        'Place kinds',
        search:       'Find places',
        running:      'Milo is looking for places nearby … This can take up to three minutes.',
        done:         'Search finished: {n} new suggestion. | Search finished: {n} new suggestions.',
        last_search:  'Last search: {when}, {result}',
        review_title: 'Suggested places',
    },
    local_event_kind: {
        concerts:  'Concerts',
        sport:     'Sport',
        culture:   'Culture',
        markets:   'Markets',
        family:    'Family',
        food:      'Food',
        festivals: 'Festivals',
        outdoors:  'Outdoors',
    },
    service: {
        services:       'Services',
        subtitle:       'The hotel’s offer on the screen (informational).',
        connector_note: 'When the PMS has services, the screen shows those; this list applies when it has none or no PMS is linked.',
        add:            'New service',
        edit:           'Edit service',
        name:           'Name',
        note:           'Description',
        group:          'Group',
        group_help:     'E.g. “Wellness” — the screen groups them.',
        price:          'Price',
        price_label:    'Price ({currency})',
        informational:  'Information only',
        informational_help: 'Show the offering without a published price or TV booking. Guests contact the team for details.',
        per_day:        '/ day',
        photo:          'Photo',
        daily:          'Per day',
        daily_help:     'The price is per day, not per visit.',
        hidden:         'Hidden',
        hidden_help:    'Not shown on the screen.',
        empty:          'There are no services yet.',
        confirm_delete: 'Delete service {name}?',
    },
    invitation: {
        title:            'Invitations',
        subtitle:         'Invite guests to a dinner, an event, a tasting … — with photos, a description and a QR code for booking.',
        add:              'New invitation',
        edit:             'Edit invitation',
        empty:            'No invitations yet.',
        limit:            'At most {max} invitations.',
        confirm_delete:   'Delete the invitation {name}?',
        kind:             'Kind',
        name:             'Title',
        summary:          'Summary',
        summary_help:     'One line on the card.',
        description:      'Description',
        texts:            'Texts',
        photos:           'Photos',
        photo:            'Main photo',
        gallery:          'Gallery ({n}/{max})',
        gallery_add:      'Add a photo',
        when:             'When',
        starts:           'Starts',
        ends:             'Ends',
        date:             'Date',
        time:             'Time',
        recurring:        'Repeats',
        recurring_help:   'E.g. "every Friday at 7 pm" — instead of a date or beside it.',
        validity:         'Shown on the screen',
        valid_from:       'From',
        valid_to:         'To',
        validity_help:    'Empty means no limit. An invitation with a date disappears by itself once the event is over.',
        price:            'Price',
        price_help:       'Free text, e.g. "€45 per person".',
        booking:          'Booking',
        cta_label:        'Call to action',
        cta_default:      'Book now',
        booking_url:      'Booking link',
        booking_url_help: 'Shown on the TV as a QR code the guest scans with a phone.',
        booking_phone:    'Phone or reception key',
        qr_preview:       'QR code on the TV',
        qr_invalid:       'The link must start with https:// or http://',
        active:           'Active',
        featured:         'Featured',
        featured_help:    'Also shown in the large panel of the home screen (the first featured one).',
        on_screen:        'On screen',
        off_screen:       'Not on screen',
        always:           'Always',
        preview:          'Card preview',
        scan:             'Scan to book',
        no_title:         '(no title)',
        end_before_start: 'The end must be after the start.',
        to_before_from:   '"To" must not be before "From".',
    },
    music: {
        title:          'Music',
        subtitle:       'Radio stations and files for the rooms and public areas.',
        add:            'New station',
        edit:           'Edit station',
        name:           'Name',
        note:           'Note',
        url:            'Address',
        source:         'Source',
        audience:       'For',
        active:         'Active',
        inactive:       'Inactive',
        ambience:       'Background music',
        ambience_help:  'Public screens play it by themselves; only one.',
        languages:      'Languages',
        languages_help: 'A guest’s screen lists the stations in their language first, then those without a language (for everyone), then the rest.',
        empty:          'There are no stations yet.',
        confirm_delete: 'Delete station {name}?',
        playback:       'Playback',
        logo:                'Logo',
        logo_help:           'Shown in the station list on the TV, at the alarm and in the guest app.',
        find_logo:           'Find logo',
        logo_found:          'Logo found — save the station.',
        logo_not_found:      'No logo found',
        logo_not_found_help: 'The public station directory has no logo large enough. Upload one yourself.',
    },
    streams: {
        title:          'Channels and stations with errors',
        subtitle:       'The errors the screens reported, and the last check of each stream (every night).',
        days:           'Last {n} days',
        check_all:      'Check all',
        check_started:  'Checking the channels and stations ({n}) …',
        check_running:  'A check is already running.',
        checking:       'Checking …',
        empty:          'No errors in the last {n} days, and everything checked works.',
        item:           'Channel or station',
        kind_channel:   'TV channel',
        kind_station:   'Radio station',
        disabled:       'off',
        errors:         'Errors',
        errors_count:   '{n}×',
        devices_count:  'on {n} device | on {n} devices',
        no_errors:      'no errors',
        last_error:     'Last error',
        last_check:     'Last check',
        open:           'Open',
        codes:          'Codes',
        load_failed:    'The overview could not be loaded.',
        retry:          'Try again',
    },
    program: {
        title:               'Programme of the day',
        subtitle:            'The programme guests see — you edit a working copy and publish it.',
        publish:             'Publish',
        published:           'The programme is published.',
        published_at:        'Published {at}',
        never_published:     'Not published yet',
        unpublished_changes: 'changes not published yet',
        closed:              'The programme comes from the PMS',
        closed_help:         'On the Hotel tab the programme source is “From the PMS” — edit the programme in the PMS.',
        standing_info:       'Standing line',
        standing_info_help:  'E.g. “Breakfast 7.00–10.00”, every day.',
        items:               'Items ({n}/20)',
        add:                 'New item',
        edit:                'Edit item',
        from:                'From',
        to:                  'To',
        time_rule:           'A time as HH:MM.',
        item_title:          'Title',
        note:                'Note',
        signup:              'Sign-ups',
        signup_help:         'The guest can sign up on the screen (needs a PMS with stays).',
        signup_count:        'Sign-ups: {n}',
        empty:               'No items for this day.',
    },
    signage: {
        title:           'Signage',
        subtitle:        'Loops for the public screens at the reception, in the lobby or at a hall.',
        add:             'New loop',
        edit:            'Edit loop',
        name:            'Name',
        note:            'Note',
        active:          'Active',
        inactive:        'Inactive',
        default_seconds: 'Default length (s)',
        slides:          'Slides',
        slide_count:     '{n} slide | {n} slides',
        devices:         'Screens',
        device_count:    '{n} screen | {n} screens',
        slide_add:       'Add a slide',
        no_slides:       'The loop has no slides yet.',
        slide_enabled:   'Enabled',
        slide_title:     'Title',
        body:            'Text',
        seconds:         'Length (s)',
        seconds_help:    '0 = the default or the video’s length.',
        from:            'From',
        to:              'To',
        photo:           'Image',
        video:           'Video',
        poster:          'Image before the video',
        muted:           'Muted',
        loop:            'Loop the video',
        empty:           'There are no loops yet.',
        assign_help:     'Give a public screen its loop on the Devices page.',
        confirm_delete:  'Delete loop {name}? The screens using it ({n}) are left without one.',
    },
    connector: {
        title:                     'PMS link',
        subtitle:                  'Where Imprion TV gets the guest, the bill, services and bookings from.',
        settings:                  'Settings',
        type:                      'System',
        none_help:                 'Without a PMS the screens show the house content, a greeting without a name and services without bookings.',
        base_url:                  'API address',
        base_url_help:             'E.g. https://pms.imprion.dev/api/pms/v1',
        token:                     'Integration key',
        token_help:                'A key issued in the PMS (Settings → Integrations).',
        token_set:                 'A key is stored. Empty keeps it.',
        property_external_id:      'PMS property ID',
        property_external_id_help: 'For information only — the key names the property.',
        test:                      'Test',
        test_ok:                   'The link works: {name}, rooms: {rooms}.',
        test_failed:               'The link does not work.',
        status:                    'Status',
        property:                  'Property',
        last_ok:                   'Last successful call',
        last_error:                'Last error',
        capabilities:              'Capabilities',
        no_capabilities:           'The PMS reports no capability.',
        rooms:                     'PMS rooms',
        rooms_help:                'Give every PMS room one of our rooms — the screen finds the guest by it.',
        match:                     'Link by name',
        matched:                   'Rooms with the same name are linked.',
        pms_room:                  'PMS room',
        our_room:                  'Imprion TV room',
        no_room:                   '— not linked —',
        bentral_help:              'Bentral sends no changes — Imprion TV reads it every 5 minutes (stays from yesterday to 30 days ahead). Bentral gives the guest\'s name and language and the stay\'s dates; it has no bill, services or room service.',
        bentral_token:             'Bentral API key',
        bentral_token_help:        'In Bentral: Settings → API. Stored encrypted and never shown again.',
        bentral_base_url_help:     'Empty = Bentral (api.bentral.com). Another address only for testing.',
        property_pick:             'Bentral property',
        property_pick_help:        'A key may reach several properties — choose the hotel whose rooms are in Imprion TV.',
        property_placeholder:      'Choose a property',
        property_required:         'Choose a property.',
        load_properties:           'Load properties',
        properties_loaded:         'The key reaches {n} property. | The key reaches {n} properties.',
        no_properties:             'The key reaches no property.',
        key_needed:                'Enter the API key first.',
        stay_statuses:             'Reservation statuses that are a stay',
        stay_statuses_help:        'Reservations with these statuses show in Occupancy and on the TV. Default: confirmed, paid, partly paid, not paid.',
        stay_statuses_required:    'Choose at least one status.',
        test_ok_bentral:           'The link works: {name}.',
        test_units:                'Room types: {types} · rooms: {rooms}',
        test_today:                'Today arrivals: {arrivals} · departures: {departures}',
        sync:                      'Last read',
        sync_never:                'not yet — the first within minutes, or press "Refresh now"',
        sync_counts:               '{stays} stays in 30 days · without a room: {unassigned} · in unlinked rooms: {unmapped}',
        sync_now:                  'Refresh now',
        synced:                    'Bentral has been read.',
        sync_failed:               'The read failed: {error}',
        group:                     'Room type',
        suggestion:                'Suggested: {room}',
        accept_suggestion:         'Link to the suggested room',
        unmapped_pms:              '{n} PMS room is not linked yet | {n} PMS rooms are not linked yet',
        unmapped_pms_help:         'Guests of these rooms do not show in Occupancy and the TV does not greet them.',
        unmapped_ours:             '{n} of our rooms has no PMS room | {n} of our rooms have no PMS room',
        all_mapped:                'All rooms are linked.',
        matched_rooms:             'Rooms with the same name or number are linked.',
    },
    import: {
        title:         'Import from Hospitality',
        subtitle:      'Channels with logos, categories, EPG, rooms, devices, the welcome and themes from the old system.',
        file:          'File',
        file_help:     'The JSON the exporter wrote (format hospitality-v1), up to 20 MB.',
        drop:          'Drop the JSON file here',
        drop_help:     'or click to choose',
        source:        'Source',
        exported_at:   'Exported',
        dry_run:       'Dry run',
        run:           'Import',
        rerun_help:    'The import can be repeated: records with the same old ID are updated, nothing is deleted.',
        confirm:       'Import the data into {name}? Existing records with the same old ID are updated.',
        done:          'The import is finished.',
        failed:        'The import failed.',
        not_json:      'The file is not valid JSON.',
        wrong_format:  'This is not a Hospitality export (expected format {format}).',
        invalid:       'The document is not valid.',
        invalid_help:  'Errors: {n}. Fix the export and try again.',
        result:        'Result',
        result_dry:    'Dry-run result (nothing written)',
        result_done:   'Imported',
        result_empty:  'Choose a file and run the dry run.',
        what:          'What',
        created:       'New',
        updated:       'Updated',
        skipped:       'Skipped ({n})',
        logo_failures: 'Logos that could not be downloaded ({n})',
        dry_run_note:  'A dry run does not download logos. If everything is right, click “Import”.',
        kind_categories:    'Categories',
        kind_channels:      'Channels',
        kind_epg_providers: 'EPG providers',
        kind_epg_mappings:  'EPG mappings',
        kind_rooms:         'Rooms',
        kind_devices:       'Devices',
        kind_themes:        'Themes',
        kind_assets:        'Logos',
    },
    tizen: {
        title:             'Samsung TV (Tizen)',
        subtitle:          'The Imprion TV app for Samsung hotel TVs: a TV installs it itself from the address below and updates it when a newer one is served.',
        install_url:       'Install address (Custom App URL)',
        install_url_help:  'In Tizen Business Manager: Entity → Profile → Default Home → Custom App URL. The TV\'s URL Launcher takes the folder: {folder}',
        copy:              'Copy the address',
        copied:            'Address copied.',
        none:              'The TV app is not published yet.',
        none_help:         'A TV finds nothing to install at this address. The app has to be built, signed and published with the room screen.',
        version:           'Published version',
        file:              'File',
        size:              'Size',
        steps_title:       'Installing on a TV',
        step_1:            'The TV in hotel mode, registered in Tizen Business Manager (Entity → Screen → Device).',
        step_2:            'Entity → Profile → Default Home: Custom App URL = the address above, Home Key Action = Hotel App.',
        step_3:            'Action → Zero Touch Configuration: attach the profile to the TV or its group.',
        step_4:            'Switch the TV off and on: it installs "Imprion TV", which shows a pairing code.',
        step_5:            'Devices (or Field → New device): enter the code and choose the room.',
        update_note:       'A TV installs a new version itself at its next power-on, once the published version is higher than its own. The room screen updates with every release, as everywhere.',
        certificate_note:  'The test certificate covers only the TVs whose DUID is in it. More TVs, and controlling the TV (power, input, volume), need a Samsung partner certificate.',
        drawer_title:      'Samsung TV',
        no_facts:          'The TV has not sent its details yet.',
        model:             'Model',
        real_model:        'Sales model',
        firmware:          'Firmware',
        tizen_version:     'Tizen',
        duid:              'DUID',
        serial:            'Serial number',
        serial_unreadable: 'not readable without a partner certificate',
        page_apis:         'APIs on the page',
        page_apis_none:    'the page does not reach the TV\'s APIs',
        boot:              'Screen start',
        boot_inapp:        'inside the TV app',
        boot_navigate:     'as a web page (fallback)',
        boot_legacy:       'as a web page (app 1.0)',
        b2b_absent:        'TV control (b2b): not available — needs a partner certificate',
        b2b_detected:      'TV control (b2b): detected (not built yet)',
        no_cec:            'No HDMI-CEC needed: the screen is the TV itself.',
        tv_itself:         'the TV itself',
    },
    apk: {
        title:                 'Android app',
        subtitle:              'Versions of the Imprion TV Android app, which devices update to by themselves — each variant on its own line.',
        upload:                'Upload APK',
        upload_help:           'The server reads the package, version, minimum Android and signing key from the APK.',
        upload_failed:         'Uploading failed.',
        uploaded_ok:           'Version {name} is uploaded (not current yet).',
        drop:                  'Drop the .apk file here',
        file:                  'File',
        not_apk:               'The file is not an .apk.',
        too_large:             'The file is larger than 200 MB.',
        version:               'Version',
        version_exists:        'This version code is already uploaded for this variant.',
        version_too_low:       'Version code {code} is lower than the highest uploaded {variant} one ({highest}). Raise versionCode and build the APK again.',
        invalid:               'The file is not a readable APK.',
        invalid_reason:        'The file is not a readable APK ({reason}).',
        wrong_package:         'This is not the Imprion TV app (package {name}).',
        unsigned:              'The APK is not signed with APK Signature Scheme v2/v3, so devices would not install it.',
        wrong_signer:          'The APK is signed with another key ({signer}), none of the allowed signing keys. Devices would not install it over the existing app.',
        read_title:            'Version {name} uploaded',
        read_help:             'This is what the server read from the APK. The version is not current yet.',
        package:               'Package',
        min_sdk:               'Minimum Android',
        api_level:             'API {level}',
        size:                  'Size',
        signer:                'Signing key',
        signer_copied:         'Key fingerprint copied.',
        code:                  'code {code}',
        notes:                 'Notes',
        make_current:          'Make current',
        now_current:           '{name} is now current — devices install it at their next check.',
        confirm_current:       'Make {name} current for the {variant} variant? Its devices install it at their next check.',
        confirm_current_older: '{name} is older than the current one. Devices with a newer one stay on theirs (the app installs only a higher code). Continue?',
        confirm_delete:        'Delete version {name}?',
        no_current_help:       'Devices of this variant get no updates until you make one current.',
        devices:               'Devices on it',
        uploaded:              'Uploaded',
        download:              'Download',
        copied:                'SHA-256 copied.',
        copy_failed:           'Copying failed.',
        empty:                 'No version uploaded yet.',
        variant:               'Variant',
        variant_all:           'All variants',
        variant_standard:      'Standard',
        variant_platform:      'Platform',
        variant_help_standard: 'Signed with the Imprion release key. Runs on every box; silent updates only as Device Owner.',
        variant_help_platform: 'Signed with the firmware platform key. Silent updates and reboot on boxes whose firmware is signed with that key.',
        current_of:            'Current · {variant}',
        no_current_of:         'No {variant} version is current',
        empty_variant:         'No {variant} version uploaded yet.',
    },
    component: {
        title:            'System components — WebView',
        subtitle:         'The WebView the room screen runs in. Platform devices allowed to install silently update it themselves, at night between 2 and 5 a.m. (or at once with “Update WebView now”), only over the installed one and only with the same signing key.',
        upload:           'Upload WebView',
        upload_help:      'The APK of the system WebView (com.android.webview or com.google.android.webview). Its signing key must be the one of the WebView on the devices, or Android refuses it.',
        uploaded_ok:      'WebView {name} uploaded.',
        read_title:       'WebView {name} uploaded',
        read_help:        'This is what the server read from the APK. Compare the signing key with the devices’ one.',
        empty:            'No WebView uploaded yet.',
        current:          'Current',
        wrong_package:    'This is not a system WebView (package {name}). Allowed are com.android.webview and com.google.android.webview.',
        version_too_low:  'Version code {code} is lower than the highest uploaded for {name} ({highest}).',
        version_exists:   'This version code is already uploaded for this package.',
        confirm_current:  'Make WebView {name} current? Platform devices install it at night with their next check.',
        confirm_current_older: 'WebView {name} is older than the current one. Devices with a newer one keep theirs. Continue?',
        now_current:      'WebView {name} is now current.',
        confirm_delete:   'Delete WebView {name}?',
        no_current:       'No {name} WebView is current — devices get no updates.',
        devices_help:     'Devices whose room screen last reported running this WebView.',
        signer_help:      'Android installs a WebView only over the installed one with the same key. Droidlogic test devices: the AOSP test key A4:0D:A8:0A … A7:1B:F5:DC.',
    },
    tile_status: {
        shown:      'On the TV',
        hidden:     'Hidden — {reason}',
        never_tile: 'Not a tile (the screen opens from elsewhere)',
        help:       'The status is for a room TV today; it refreshes when you save.',
        reason: {
            merged_into_house:   'inside the House tile',
            merged_into_tv:      'inside the Television tile',
            no_services:         'no visible services',
            no_local:            'no events or places',
            program_unpublished: "today's programme is not published",
            no_program:          'no programme today',
            needs_connector:     'needs a PMS connection',
            no_channels:         'no enabled channels',
            no_stations:         'no active stations for rooms',
            no_partners:         'the PMS has no partners with an offer',
            no_invitations:      'no invitation on screen now',
            no_room_service:     'no room service (neither the PMS one nor your own)',
            milo_off:            'Milo is off on room TVs',
            no_requests:         'the guest has no requests yet',
            no_room_guide:       '"Your room" has no content yet',
            feedback_off:        'guest feedback is switched off',
            alarm_on_clock:      'opened from the clock at the top (theme)',
        },
        explain: {
            merged_into_house: 'The welcome letter is part of the House screen while the House tile is shown.',
            merged_into_tv:    'The radio stations are on the Television screen while that tile is shown.',
            milo_off:          'The tile appears once Milo is on for room TVs (Milo › Settings) and the provider has a key.',
            no_requests:       'The tile appears on the TV by itself once the guest in the room has an order or a booking — hidden while nobody is checked in.',
            feedback_off:      'The tile appears once an admin switches guest feedback on (Praise, complaints and suggestions › Settings).',
        },
        fix: {
            services:  'Content › Services',
            hotel:     'Content › Hotel (Area)',
            program:   'Content › Programme',
            music:     'Content › Music',
            channels:  'TV › Channels',
            connector: 'PMS connection',
            invitations: 'Content › Invitations',
            room_service: 'Room service',
            milo:      'Milo › Settings',
            room_guide: 'Content › Your room',
            feedback:  'Praise, complaints and suggestions › Settings',
            themes:    'Content › Themes',
        },
    },
    tile_size: {
        auto:  'Automatic',
        small: 'Small 1×1',
        wide:  'Wide 2×1',
        tall:  'Tall 1×2',
        large: 'Large 2×2',
    },
    home_grid: {
        title:         'Home screen layout',
        size:          'Grid {columns} × {rows}',
        estimate:      'Estimate — not saved',
        auto:          'Every shown tile is "Automatic": the frame (the default look or the active theme) lays the tiles out. The grid switches on as soon as one tile has a size.',
        overflow_title: '{n} tile does not fit on the screen | {n} tiles do not fit on the screen',
        does_not_fit:  'Does not fit on the screen — make it smaller or hide the tile.',
        frame_none:    'Default look',
        sidebar_mode:  'The active theme shows the sidebar with one large card: tile sizes do not apply in this mode, every shown tile is in the list.',
    },
    screen_mode: {
        light: 'Light',
        dark:  'Dark',
    },
    screen_navigation: {
        sidebar: 'Sidebar',
        cards:   'Cards',
        both:    'Both',
    },
    screen_alarm_entry: {
        tile:  'Tile / row in the menu',
        clock: 'Only the clock at the top',
    },
    screen_alarm_entry_help: {
        tile:  'The alarm (»Prebujanje po vaše«) is one of the home screen\'s tiles, as before.',
        clock: 'No alarm tile: the guest moves from the languages to the clock in the screen\'s header and sets the alarm with OK. An alarm-clock icon stands beside the time.',
    },
    screen_navigation_help: {
        sidebar: 'Every tile listed on the left, one large card of the chosen one on the right with a preview of its content (Wi-Fi, what is on, an invitation …). Tile sizes do not apply here.',
        cards:   'No left column: the cards across the whole home screen, the logo in the header.',
        both:    'The left column and the cards together — every tile is on the screen twice.',
    },
    screen_qr_position: {
        welcome: 'With the welcome',
        header:  'Top right, under the clock',
        sidebar: 'At the foot of the side column',
        hidden:  'Hidden',
    },
    screen_room_label_position: {
        sidebar: 'In the side column, under the logo',
        header:  'Beside the clock, top right',
        welcome: 'On the line with the departure date',
    },
    screen_layout: {
        rows:       'Rows',
        rows_brand: 'Rows with brand column',
        sidebar:    'Sidebar',
        strip:      'Strip',
        split:      'Split',
        classic:    'Original',
    },
    screen_layout_help: {
        rows:       'Full-width header, the welcome, a first row of large cards, the rest beneath (Natura, Rural).',
        rows_brand: 'A brand column with the logo and motto on the left, rows on the right (Heritage).',
        sidebar:    'A navigation column listing every tile on the left, the welcome and cards on the right (Alpine, Ember).',
        strip:      'The background picture full-bleed, every tile in one bottom row (Riviera).',
        split:      'Content in the left two thirds, a tall picture in the right third (Metropolitan).',
        classic:    'The room screen\'s own original look: the welcome and tiles on the left, the forecast and the featured invitation on the right (Original). Only the ground, panels, accent, text, corners and type are drawn from the theme.',
    },
    screen_card: {
        caption_bar: 'Photo with caption bar',
        overlay:     'Text over photo',
        icon:        'Icon, no photo',
        photo_top:   'Photo on top',
        photo_fade:  'Fading photo',
        list:        'List row',
    },
    screen_mark: {
        none:      'None',
        dot:       'Dot',
        arrow:     'Arrow',
        arrow_all: 'Arrow on every card',
    },
    screen_mark_help: {
        none:      'Only the outline marks the focused card.',
        dot:       'A small accent dot in the focused card\'s corner (Natura).',
        arrow:     'A round arrow button on the focused card (Metropolitan, Heritage, Rural).',
        arrow_all: 'An arrow on every card, in the accent on the focused one (Alpine).',
    },
    screen_ornament: {
        none:              'None',
        leaves:            'Leaves',
        copper_glow:       'Copper glow',
        mountains_lineart: 'Mountains (line art)',
        arches_lineart:    'Arches (line art)',
        wheat_flowers:     'Wheat and flowers',
    },
    screen_ornament_help: {
        none:              'No ornament.',
        leaves:            'A soft branch in the top corner and at the foot (Natura).',
        copper_glow:       'A warm glow behind the header (Ember).',
        mountains_lineart: 'A ridge line at the foot of the navigation column (Alpine).',
        arches_lineart:    'Arches and a column in the lower brand column (Heritage).',
        wheat_flowers:     'Wheat stalks and meadow flowers up both side edges (Rural).',
    },
    logo_treatment: {
        as_is:       'As uploaded',
        light_plate: 'On a light plate',
        inverted:    'White silhouette',
    },
    logo_treatment_help: {
        as_is:       'The hotel\'s logo is drawn as uploaded.',
        light_plate: 'On a small surface-coloured plate — a dark logo on a dark theme.',
        inverted:    'As a white silhouette — for a dark column or ground.',
    },
    screen_font_kind: {
        serif: 'serif',
        sans:  'sans',
    },
    screen_color: {
        background:              'Ground',
        background_alt:          'Ground\'s warm corner',
        surface:                 'Surface',
        surface_alt:             'Second surface',
        surface_border:          'Surface border',
        surface_text:            'Titles on a surface',
        surface_text_muted:      'Subtitles on a surface',
        heading:                 'Welcome and headings',
        text:                    'Text',
        text_muted:              'Secondary text',
        accent:                  'Accent',
        accent_text:             'Text on the accent',
        focus_ring:              'Focused card outline',
        nav_background:          'Column background',
        nav_text:                'Titles in the column',
        nav_text_muted:          'Subtitles and motto in the column',
        nav_active:              'Selected item bar',
        nav_active_background:   'Selected item row',
        card_caption_background: 'Caption bar',
        card_caption_text:       'Caption title',
        card_caption_text_muted: 'Caption subtitle',
        overlay_text:            'Text over a photo',
        overlay_scrim:           'Shade under that text',
        chip_background:         'Chip',
        chip_border:             'Chip border',
        chip_text:               'Chip text',
        chip_active_background:  'Selected chip',
        chip_active_border:      'Selected chip border',
        chip_active_text:        'Selected chip text',
        divider:                 'Divider',
        ornament:                'Ornament',
    },
    screen_color_help: {
        background:              'The ground of every screen (the letterbox too).',
        background_alt:          'The ground is a gradient from this corner into the ground.',
        surface:                 'A card or panel face.',
        surface_alt:             'Inset rows, an icon well, the side panel without a picture.',
        surface_border:          'A card\'s hairline.',
        surface_text:            'Titles on a card.',
        surface_text_muted:      'Subtitles on a card.',
        heading:                 'The welcome, the house name, screen titles.',
        text:                    'Body text on the ground.',
        text_muted:              'The subtitle, the date, key hints.',
        accent:                  'Section titles, icons, prices, the focused card\'s mark.',
        accent_text:             'Text or an arrow on the accent.',
        focus_ring:              'The outline of the card the remote is on.',
        nav_background:          'The navigation column and the brand column.',
        nav_text:                'Titles in the column, an inverted logo in it.',
        nav_text_muted:          'Subtitles and the motto in the column.',
        nav_active:              'The bar beside the selected item.',
        nav_active_background:   'The selected item\'s row.',
        card_caption_background: 'The band under a "photo with caption bar" card\'s photo.',
        card_caption_text:       'The title in that band.',
        card_caption_text_muted: 'The subtitle in that band.',
        overlay_text:            'Text over a photo ("text over photo" cards, the welcome on a strip).',
        overlay_scrim:           'The gradient under that text, top to bottom.',
        chip_background:         'Language chips, the room chip, drawn remote keys.',
        chip_border:             'Their outline.',
        chip_text:               'Their text.',
        chip_active_background:  'The selected language, the OK key.',
        chip_active_border:      'Its outline.',
        chip_active_text:        'Its text.',
        divider:                 'Hairlines and separators.',
        ornament:                'The ornament\'s strokes.',
    },
    screen_opacity: {
        surface:            'Opacity',
        card_caption:       'Opacity',
        overlay_scrim_from: 'Top',
        overlay_scrim_to:   'Bottom',
        ornament:           'Opacity',
    },
    season: {
        title:              'Seasonal themes',
        subtitle:           'Milo turns a base theme into a look for an occasion or a mood — colours, background, decoration, greeting and animation. The room screens show it by themselves in its period and go back to the base theme afterwards.',
        create:             'New seasonal theme',
        create_title:       'New seasonal theme',
        create_help:        'Choose the occasion, the base theme, the period and what Milo may adapt. The theme is saved at once; you can refine it in the theme editor later.',
        regenerate_title:   'Make again',
        regenerate_help:    'Milo makes only the chosen parts again; the others stay as they are.',
        occasion:           'Occasion',
        prompt:             'Mood',
        prompt_help:        'Optional with an occasion, required with “Custom”. E.g. “warm, family, golden accents”.',
        prompt_placeholder: 'E.g. elegant, calm, a winter forest …',
        prompt_required:    'Describe the mood.',
        base:               'Base theme',
        base_help:          'The layout, cards and fonts stay the base theme’s.',
        own_theme:          'own',
        from:               'From',
        to:                 'To',
        to_before_from:     'The end is before the start (for a period over New Year switch on “Every year”).',
        yearly:             'Every year',
        yearly_help:        'Repeats every year on the same days; the period may run over New Year (24 Dec–6 Jan).',
        scheduled:          'Switch on by schedule',
        scheduled_help:     'The screens show the theme in its period by themselves. Off: the theme stays ready.',
        parts:              'What Milo may adapt',
        parts_help:         'Parts not chosen stay as in the base theme. Each part can be switched on or off later without making it again.',
        parts_regenerate_help: 'Milo makes only the checked parts again (and switches them on).',
        parts_required:     'Choose at least one part.',
        no_image_provider_ahead: 'A background picture cannot be made now: the provider has not entered an image key (OpenAI) in Provider › Milo. Colours, decoration, greeting and animation are made all the same.',
        generate:           'Make with Milo',
        regenerate:         'Make again',
        working:            'Milo is making the theme …',
        working_help:       'Usually up to half a minute, with a picture up to two minutes. You may close this — Milo keeps working.',
        working_short:      'Milo is working',
        step_palette:       'Colours, decoration, greeting and animation',
        step_image:         'Background picture',
        step_saving:        'Saving',
        failed:             'Milo could not make the theme',
        failed_short:       'Failed',
        failure_provider:   'The AI provider answered an error. Try again in a few minutes.',
        failure_timeout:    'The AI provider did not answer in time. Try again.',
        failure_refused:    'The AI provider declined the request. Change the mood and try again.',
        failure_bad_answer: 'Milo’s answer could not be used (e.g. an invalid colour). Try again.',
        failure_interrupted: 'The work was interrupted (the server restarted). Try again.',
        load_failed:        'The seasonal themes could not be read.',
        bases_failed:       'The themes to start from could not be read.',
        follow_failed:      'The progress could not be read; Milo keeps working — the list shows where it is.',
        try_again:          'Try again',
        open_editor:        'Edit in the theme editor',
        done:               'Done',
        image_no_provider:  'No background picture: the provider has not entered an image key (OpenAI) in Provider › Milo. The background stays the base theme’s; once the key is entered, choose “Make again” with only “Background”.',
        image_no_provider_kept: 'No new background picture: the provider has not entered an image key (OpenAI) in Provider › Milo. Milo’s previous picture stays.',
        image_failed_kept:  'A new background picture could not be made ({reason}); Milo’s previous picture stays.',
        image_failed_reason: 'The background picture could not be made ({reason}); the background stays the base theme’s.',
        contrast_fixed:     '{n} text colour was adjusted for readability. | {n} text colours were adjusted for readability.',
        parts_missing:      'On but not made yet: {list}. Make them with “Make again”.',
        in_force:           'In force',
        every_year:         'every year',
        based_on:           'from {name}',
        activation:         'Show',
        parts_on:           'Parts on',
        preview:            'Preview',
        now:                'The screens now show “{name}”',
        now_none:           'The screens now show the base theme',
        precedence:         'Where periods overlap, the theme with the shorter period wins (a Christmas week inside winter), then the one that started later. A manual “On” beats every schedule; “Off” is never shown.',
        empty:              'No seasonal themes yet. With “New seasonal theme” Milo makes the first one.',
        confirm_delete:     'Delete the seasonal theme “{name}”? Milo’s background picture is deleted with it.',
        error_not_configured: 'Milo is not set up: the provider has not entered an AI key yet.',
        error_limit:        'Today’s limit for Milo is used up. Try again tomorrow.',
        error_failed:       'Milo could not make the theme.',
        error_busy:         'Milo is already making this theme.',
        greeting_edit:      'Edit greeting',
        greeting_title:     'Greeting on the room screen',
        greeting_help:      'The line under the welcome, in each of the hotel\'s languages (up to 90 characters). »Make again« of the other parts keeps your wording; only checking »Greeting« replaces it.',
        greeting_off:       'The »Greeting« part is off — the screens show no greeting. Switch it on among the parts on the card.',
        greeting_saved:     'The greeting is saved.',
        greeting_replace:   'You edited the greeting yourself — with »Greeting« checked Milo replaces it with its own.',
        step_tiles:         'Decorations on the tile pictures ({done} of {total})',
        no_image_provider_tiles_ahead: 'Tile pictures cannot be made now: the provider has not entered an image key (OpenAI) in Provider › Milo. The tiles keep their own pictures.',
        tiles_title:        'Tile pictures',
        tiles_help:         'Left the hotel’s picture, right with decorations. The screens show the decorated ones only while the theme is in force; the originals stay unchanged.',
        tiles_off:          'The “Tile pictures” part is off — the screens show the original pictures.',
        tiles_empty:        'No decorated tile pictures yet.',
        tile_original:      'Original',
        tile_decorated:     'Decorated',
        tile_stale:         'New picture',
        tile_stale_help:    'The tile’s picture was replaced since — the screens show the new one. “Make again” decorates it.',
        tile_regenerate:    'Make again',
        tile_remove:        'Remove decoration',
        confirm_tile_remove: 'Remove the decoration from the “{name}” tile? The screens will show the original picture.',
        tile_removed:       'The decoration is removed.',
        tiles_no_provider:  'No tile pictures: the provider has not entered an image key (OpenAI) in Provider › Milo. The tiles keep their own pictures.',
        tiles_failed:       'Some tile pictures could not be made: {list}.',
        tiles_skipped:      'Skipped: {list}. A tile gets its picture in Content › Home screen.',
        tiles_none:         'No tile could be decorated — none has a picture. A tile gets its picture in Content › Home screen.',
    },
    season_occasion: {
        christmas:  'Christmas',
        new_year:   'New Year',
        easter:     'Easter',
        valentines: 'Valentine’s Day',
        halloween:  'Halloween',
        spring:     'Spring',
        summer:     'Summer',
        autumn:     'Autumn',
        winter:     'Winter',
        custom:     'Custom',
    },
    season_part: {
        colors:     'Colours',
        background: 'Background',
        ornament:   'Decoration',
        greeting:   'Greeting',
        animation:  'Animation',
        tiles:      'Tile pictures',
    },
    season_part_help: {
        colors:     'Palette and transparency, with readable contrast.',
        background: 'A decorative background picture, fitted to the layout.',
        ornament:   'The theme’s ornament (branches, glow, …).',
        greeting:   'A line under “Welcome” in every language of the hotel.',
        animation:  'A gentle animation: snow, leaves, sparkles, bats …',
        tiles:      'Decorations for the occasion on the home tiles’ pictures (up to 12 pictures); the hotel’s pictures stay.',
    },
    season_tile_skip: {
        no_picture: 'no picture',
        milo:       'Milo has no picture',
        limit:      'more than 12',
    },
    season_activation: {
        schedule: 'By schedule',
        on:       'On',
        off:      'Off',
    },
    screen_theme: {
        title:          'Room-screen themes',
        subtitle:       'The whole look of the room screen: colours, fonts, the home layout, cards, ornament and background.',
        none:           'No theme',
        active_now:     'Active theme: {name}',
        none_active:    'No theme — the room screen has its default look.',
        overrides_help: 'The colours, corners and logo from General › Appearance stay the hotel\'s and apply over a theme too; the font and light/dark ground are the theme\'s.',
        admin_chooses:  'The hotel\'s administrator chooses the theme. You can create and edit your own themes.',
        library:        'Library',
        own:            'Your themes',
        own_empty:      'You have no theme of your own yet. Pick one from the library and "Duplicate and edit".',
        use:            'Use',
        duplicate:      'Duplicate',
        duplicate_edit: 'Duplicate and edit',
        view:           'View',
        active:         'Active',
        based_on:       'from {key}',
        used_by:        '{n} tenant | {n} tenants',
        confirm_delete: 'Delete the theme "{name}"?',
        confirm_delete_used: 'The theme "{name}" is active at {n} tenants and cannot be deleted while they use it. Try anyway?',
        activated:      'The theme "{name}" is now on the room screens.',
        deactivated:    'The room screens have their default look again.',
        duplicated:     'Copy "{name}" created.',
        key_taken:      'The key "{key}" is already taken.',
        library_title:    'Themes (library)',
        library_subtitle: 'The builtin themes every tenant sees and duplicates into its own.',
        library_empty:    'The library has no themes.',
        library_help:     'A change to a builtin reaches every tenant that has it active within a minute. Tenants\' copies stay as they are.',
        new:              'New theme',
        back:             'Back',
        unsaved:          'Not saved',
        use_after_save:   'Save your changes first.',
        leave_unsaved:    'The theme has unsaved changes. Leave the editor?',
        leave:            'Leave',
        builtin_read_only:      'A builtin theme of the library',
        builtin_read_only_help: 'Builtin themes cannot be changed. Duplicate it and edit your copy.',
        editor:           'Theme',
        editor_title:     'Theme "{name}"',
        editor_subtitle:  'The room screen\'s look.',
        editor_meta:      '{scope} · key {key} · revision {revision}',
        scope_builtin:    'Library',
        scope_tenant:     'Your theme',
        section_about:    'About',
        section_colors:   'Colours',
        section_fonts:    'Fonts',
        section_layout:   'Layout',
        section_ornament: 'Ornament',
        section_ground:   'Background',
        section_logo:     'Logo',
        name:             'Name',
        name_help:        'In at least one language.',
        description:      'Description',
        key:              'Key',
        key_help:         'Lowercase letters, digits and hyphens, 2–40 characters; never changes later.',
        key_fixed:        'The key never changes.',
        order:            'Order in the library',
        order_help:       '0–1000; lower comes first.',
        preview_picture:  'Gallery picture',
        preview_picture_help: 'A screenshot in this theme, shown on its gallery card.',
        based_on_help:    'A copy of the theme "{key}".',
        mode:             'Ground',
        mode_help:        'Light or dark — every screen follows it.',
        group_ground:     'Ground and surfaces',
        group_text:       'Text and accent',
        group_nav:        'Navigation column',
        group_caption:    'Card captions and overlay',
        group_chips:      'Chips',
        group_divider:    'Divider and ornament',
        display_family:   'Heading font',
        display_family_help: 'The welcome, headings and tile titles.',
        display_weight:   'Heading weight',
        letter_spacing_display: 'Letter spacing of the welcome',
        body_family:      'Body font',
        body_family_help: 'Everything else; a sans or the system font only.',
        letter_spacing_label: 'Letter spacing of labels',
        letter_spacing_label_help: 'Small capital labels and the motto.',
        font_real:        'The screen\'s font ({name})',
        font_approx:      'Approximation — the room screen\'s {name} file cannot be loaded here',
        font_loading:     'Loading {name} …',
        fonts_approx:     'The preview draws {list} with an approximation: the room screen\'s files are not reachable.',
        layout:           'Home screen layout',
        hero_tiles:       'Cards in the first row',
        hero_tiles_help:  'How many tiles share the first row — as many as the layout allows.',
        hero_n:           '{n}',
        navigation:       'Navigation',
        navigation_help:  'How a guest chooses: in the sidebar with one large card, with cards, or both.',
        navigation_cards_only: 'This layout has no left column, so only cards are available.',
        qr_position:      'QR codes position (Wi-Fi and app)',
        qr_position_help: 'Where the Wi-Fi and phone-app codes stand on the home screen. They always stay far enough apart for a guest to aim the phone at one.',
        room_label_position:      'Room label position',
        room_label_position_help: 'Where the room\'s name (e.g. "Cottage 1") stands, with the bed icon.',
        order_ticker:      'Show open orders',
        order_ticker_help: 'Keeps the guest\'s open orders and requests on screen at all times, with a countdown.',
        alarm_entry:       'Alarm on the home screen',
        alarm_entry_help:  'How the guest reaches the wake-up alarm on the room TV. The clock in the screen\'s header opens it either way (OK on the remote, or a click).',
        position_own:      'default',
        position_fallback: 'This layout has no such place — the screen draws it at the default place: {place}.',
        card_hero:        'First card',
        card_secondary:   'Second card',
        card_rest:        'Other cards',
        cards_help:       '"List row" is for the other cards only. A tile without a photo is drawn as an icon in a photo style.',
        mark:             'Focused card mark',
        radius:           'Card corners',
        radius_help:      '0 = all square; chips and keys follow.',
        ornament:         'Ornament',
        ornament_help:    'The ornament\'s colour and opacity are in Colours › Divider and ornament.',
        background_kind:  'Background',
        background_color: 'Theme colours',
        background_image: 'Picture',
        background_color_help: 'The ground is a gradient of "Ground\'s warm corner" and "Ground" (Colours).',
        background_picture: 'Background picture',
        background_picture_help: 'In "Split" the picture is the right panel, in "Strip" it is full-bleed.',
        scrim:            'Veil over the picture',
        scrim_help:       'The ground colour over the picture, so text stays readable.',
        logo_treatment:   'Hotel logo',
        logo_treatment_help: 'The hotel uploads its logo in General › Appearance; without one the monogram sits on the accent.',
        preview:          'Preview',
        preview_help:     'The 1920 × 1080 home screen in this theme. What the setting under the pointer or being edited changes is outlined in red; click a part of the screen to go to the settings that draw it.',
        highlight_unseen: 'Not shown in this layout',
        preview_note:     'The console draws the preview by the theme\'s rules; the room screen draws the same with its own icons and ornament.',
        preview_welcome:  'Welcome',
        preview_welcome_sub: 'Everything for a pleasant stay in one place.',
        preview_room:     'Room 204',
        preview_qr_wifi:  'Wi-Fi: Hotel-Guest',
        preview_qr_app:   'The hotel on your phone',
        preview_select:   'Select',
        preview_open:     'Open',
        preview_body:     'Breakfast is served from 7 to 10 a.m. in the restaurant on the ground floor.',
        preview_label:    'Concept · Tradition · Comfort',
        preview_hotel:    'Your hotel',
        preview_tagline:  'Tradition · Comfort',
        apply_overrides:  'With the hotel\'s adjustments ({list})',
        override_accent:  'accent',
        override_primary: 'primary colour',
        override_radius:  'square corners',
        sample_photos:    'Stand-in pictures for tiles without a photo',
        provider_assets:  'Provider pictures',
        hex_rule:         'A colour as #RRGGBB.',
        key_rule:         'Lowercase letters, digits and hyphens; starts with a letter; 2–40 characters.',
        name_required:    'Enter a name in at least one language.',
        hero_rule:        'This layout allows another number.',
        navigation_rule:  'This layout does not allow this navigation.',
        weight_rule:      'This font has no such weight.',
        body_rule:        'The body may only be a sans or the system font.',
        picture_required: 'Choose a background picture.',
        fix_errors:       'Fix the marked fields.',
        field_refused:    'The server did not accept this value.',
        sample: {
            services: 'Restaurant',
            local:    'Experiences',
            house:    'Your stay',
            program:  'Today\'s programme',
            tv:       'TV and music',
        },
        sample_sub: {
            services:     'Discover the dining',
            local:        'Explore the area',
            house:        'Information and contacts',
            program:      'Events and notices',
            tv:           'Channels, radio and alarm',
            letter:       'Welcome letter',
            bill:         'Your bill',
            music:        'Radio stations',
            alarm:        'Alarm',
            room_service: 'Room service',
            nearby:       'Nearby offers',
            invitations:  'House invitations',
            cast:         'Apps',
            milo:         'Ask the hotel assistant',
            requests:     'Orders and bookings',
            room_guide:   'Where to find it, how it works, minibar',
            feedback:     'Tell us how you are enjoying your stay',
        },
    },
    requests: {
        title:                  'Guest requests',
        subtitle:               'Invitation bookings, room-service orders, housekeeping and requests to the reception from the TV and the guest\'s phone',
        subtitle_kitchen:       'Room-service orders from the TV and the guest\'s phone',
        subtitle_housekeeping:  'What guests are missing or what does not work in the room — from the TV and the guest\'s phone',
        done:                   'Done',
        housekeeping_free:      'No description',
        housekeeping_from_guide: 'From "Your room"',
        housekeeping_own_words: 'The guest\'s own words',
        reception_now:          'now',
        reception_nights:       'Nights: {n}',
        reception_persons:      'Persons: {n}',
        reception_time:         'Time',
        reception_departure:    'Booked departure',
        reception_help:         'Arrange it at the reception (in the PMS too) — Imprion TV changes nothing by itself.',
        reason_not_available:   'Sorry, we do not have that at the moment.',
        reason_technician:      'We have called maintenance, they will come as soon as possible.',
        live:                   'Live',
        offline:                'Not connected',
        sound_on:               'Sound on',
        sound_off:              'Sound off',
        notifications_enable:   'Enable notifications',
        view_open:              'Open',
        view_all:               'All (last 2 days)',
        kind_all:               'All kinds',
        none_open:              'No open requests.',
        none:                   'No requests in the last two days.',
        pick:                   'Pick a request from the list.',
        asap:                   'as soon as possible',
        persons_n:              '{n} pers.',
        persons:                'Persons',
        slot:                   'Time',
        desired:                'Wanted at',
        proposed_at:            'Proposed time',
        invitation:             'Invitation',
        created:                'Sent',
        payment:                'Payment',
        payment_room:           'Charged to the room',
        total:                  'Total',
        note:                   'Guest\'s note',
        reason:                 'Reason',
        history:                'History',
        by_guest:               'guest',
        accept:                 'Accept',
        accept_other_time:      'Accept for another time',
        accept_other_time_help: 'The guest sees on the TV and the phone that the request is accepted for this time.',
        decline:                'Decline',
        decline_title:          'Decline request #{number}',
        decline_help:           'The guest reads the reason on the TV and the phone.',
        on_the_way:             'On the way',
        delivered:              'Delivered',
        reason_sold_out:        'Sorry, it is sold out.',
        reason_kitchen_closed:  'The kitchen is closed.',
        reason_call_reception:  'Please call reception.',
        reason_full:            'The time is fully booked.',
        reason_closed:          'We take no bookings that day.',
        delivery_line:          'Delivery',
        eta:                    'Ready around',
        eta_in:                 'in {n} min',
        eta_at_time:            'at …',
        eta_pick:               'When will it be ready?',
        accept_eta:             'Accept — in {n} min',
        accept_as_asked:        'Accept (at {time})',
        accept_at_title:        'Accept — ready at',
        accept_at_help:         'The guest sees on the TV and the phone that the order will be ready around this time.',
        eta_at:                 'Ready at',
        late_cancel:            'Guest cancelled (too late)',
        late_cancel_help:       'The guest cancelled the order because the accepted time was later than they asked for. Do not prepare it.',
        cancel_open:            'Guest may cancel until {time}',
        cancel_open_help:       'The accepted time is later than asked, so the guest may still cancel the order.',
    },
    request_status: {
        pending:    'Waiting',
        accepted:   'Accepted',
        declined:   'Declined',
        on_the_way: 'On the way',
        delivered:  'Delivered',
        cancelled:  'Cancelled',
        done:       'Done',
    },
    request_kind: {
        booking:      'Booking',
        room_service: 'Room service order',
        housekeeping: 'Housekeeping',
        reception:    'Reception',
    },
    reception_type: {
        late_checkout: 'Late check-out',
        extra_night:   'Extra night',
        taxi:          'Taxi',
        table:         'Restaurant table',
    },
    housekeeping_type: {
        missing_item: 'Missing',
        extra_item:   'I need',
        replace:      'Replace',
        broken:       'Not working',
    },
    feedback_status: {
        new:         'New',
        in_progress: 'In progress',
        resolved:    'Resolved',
    },
    feedback_kind: {
        praise:     'Praise',
        complaint:  'Complaint',
        suggestion: 'Suggestion',
    },
    feedback_category: {
        room:        'Room',
        cleanliness: 'Cleanliness',
        food:        'Food',
        staff:       'Staff',
        noise:       'Noise',
        wifi_tv:     'Wi-Fi/TV',
        other:       'Other',
    },
    feedback_preset: {
        room_comfortable: 'The room is very comfortable',
        room_spotless:    'Everything is spotless',
        food_delicious:   'The food is excellent',
        staff_friendly:   'The staff are very friendly',
        quiet_room:       'The room is quiet',
        wifi_fast:        'Wi-Fi and TV work great',
        would_return:     'We\'d love to come back',
        room_temperature: 'The room is too hot or too cold',
        room_not_clean:   'The room is not clean',
        food_slow:        'The food came late or cold',
        staff_waiting:    'I waited too long',
        noise_night:      'It is noisy at night',
        wifi_slow:        'The Wi-Fi is slow',
        tv_not_working:   'The TV doesn\'t work',
        something_broken: 'Something is broken',
        breakfast_choice: 'More choice at breakfast',
        coffee_in_room:   'A coffee maker in the room',
        towels_on_request: 'Towels changed only on request',
        soundproofing:    'Better soundproofing',
        more_channels:    'More TV channels',
        local_tips:       'More tips for trips nearby',
        eco_friendly:     'More eco-friendly choices',
    },
    review_site: {
        google:      'Google',
        tripadvisor: 'Tripadvisor',
        booking:     'Booking.com',
        facebook:    'Facebook',
        instagram:   'Instagram',
    },
    review_invite: {
        after_feedback: 'After sending feedback',
        departure_day:  'On the departure day',
        after_checkout: 'After check-out (phone)',
    },
    review_invite_help: {
        after_feedback: 'After any praise, complaint or suggestion the guest sees the "Rate us" links — prominently after praise, quieter below the confirmation after a complaint or a suggestion.',
        departure_day:  'From the chosen time on the evening before departure until the end of the departure day the TV shows a quiet notice with QR codes once a day, the phone a card on Home.',
        after_checkout: 'When the phone\'s link ends at check-out, its "link expired" screen offers the review links.',
    },
    feedback: {
        title:               'Praise, complaints and suggestions',
        subtitle:            'Guest feedback from the TV and the phone — complaints during the stay first',
        tab_inbox:           'Overview',
        tab_analysis:        'Analysis',
        tab_settings:        'Settings',
        summary_title:       'Last {n} days',
        tile_rating:         'Average praise rating',
        tile_rating_hint:    'from {n} ratings',
        review_clicks:       '"Rate us" clicks',
        from_tv:             'TV',
        from_phone:          'Phone',
        from_checkout:       'After check-out',
        filter_status:       'Status',
        filter_kind:         'Kind',
        filter_category:     'Category',
        filter_days:         'Period',
        filter_help:         'Open ones are always listed.',
        all_statuses:        'All statuses',
        open_only:           'Open only',
        all_kinds:           'All kinds',
        all_categories:      'All categories',
        last_days:           'Last {n} days',
        none:                'No feedback matches the selection.',
        pick:                'Pick a feedback from the list.',
        during_stay:         'Guest still in the room',
        during_stay_help:    'You can put it right before the guest leaves — call or stop by the room.',
        nothing_said:        'Rating only',
        replied:             'Replied',
        rating_n:            'Rated {n} of 5',
        no_rating:           'no rating',
        status:              'Status',
        photo_n:             'Photo {n}',
        reply:               'Reply to the guest',
        replied_by:          '{name}, {when}',
        reply_placeholder:   'E.g. "We apologise, the room has been put right."',
        reply_help:          'The guest sees the reply on the phone and as a notice on the room TV. A new feedback moves to in progress.',
        reply_help_gone:     'The guest has left — the reply is only seen if their phone link is still open.',
        reply_send:          'Send reply',
        reply_replace:       'Replace reply',
        reply_sent:          'The reply has been sent.',
        reply_draft:         'Suggest a reply',
        reply_draft_help:    'The AI writes the draft in the guest\'s language — read and correct it before sending.',
        reply_draft_replace: 'A new draft will replace the text you wrote. Continue?',
        reply_draft_confirm: 'Replace with a draft',
        notes:               'Internal notes',
        note_placeholder:    'A note for the team (the guest never sees it)',
        note_add:            'Add',
        expires:             'Deleted on {when}.',
    },
    feedback_analysis: {
        open_analysis:          'Analysis',
        period_help:            'The same period as the list in Overview; open complaints count whatever their age.',
        empty_title:            'No feedback',
        empty:                  'Guests sent no praise, complaints or suggestions in the last {n} days.',
        tile_total:             'All feedback',
        tile_total_hint:        'praise: {praise} · suggestions: {suggestions}',
        tile_complaints:        'Complaints',
        tile_complaints_hint:   '{share} of all',
        tile_open:              'Open complaints',
        tile_open_hint:         'new and in progress, sent any time',
        tile_open_here:         'guest still in the room: {n}',
        tile_overdue:           'No reply > {h} h',
        tile_overdue_hint:      'open complaints waiting for a reply to the guest',
        tile_first_reply:       'First reply (median)',
        tile_first_reply_hint:  '{n} of {m} complaints replied',
        tile_resolve:           'To resolve (median)',
        tile_resolve_hint:      '{n} of {m} complaints resolved',
        no_complaints_short:    'no complaints in the period',
        delta:                  '{change} vs the previous {n} days',
        delta_same:             'same as the previous {n} days',
        under_minute:           'under 1 min',
        minutes:                '{m} min',
        hours:                  '{h} h {m} min',
        days:                   '{d} d {h} h',
        trend_title:            'Trend',
        trend_subtitle:         'Praise and suggestions above the line, complaints below it',
        legend_praise:          'Praise',
        legend_suggestions:     'Suggestions',
        legend_complaints:      'Complaints',
        bucket_day:             'per day',
        bucket_week:            'per week (from Monday)',
        bucket_month:           'per month',
        trend_aria:             'Trend over {n} periods: praise {praise}, suggestions {suggestions}, complaints {complaints}. The left and right arrows show one period.',
        period:                 'Period',
        show_table:             'Table',
        show_chart:             'Chart',
        category_title:         'By category',
        category_subtitle:      'Sorted by the number of complaints',
        legend_complaints_left: 'Complaints (left)',
        legend_praise_right:    'Praise (right)',
        legend_suggestions_right: 'Suggestions (right, after praise)',
        complaints_short:       'Complaints',
        praise_short:           'Praise',
        praise_suggestions_short: 'Praise · suggestions',
        rating_short:           'Rating',
        row_counts:             'complaints {complaints}, praise {praise}, suggestions {suggestions}',
        row_rating:             'praise rating {rating} ★',
        no_feedback_in:         'No feedback: {list}.',
        topics_title:           'What guests choose most',
        topics_subtitle:        'Ready sentences from the TV and the phone',
        top_complaints:         'In complaints',
        top_praise:             'In praise',
        top_suggestions:        'In suggestions',
        top_none:               'No ready sentence was chosen.',
        share_of_complaints:    'in {share} of complaints',
        share_of_praise:        'in {share} of praise',
        share_of_suggestions:   'in {share} of suggestions',
        rooms_title:            'Rooms',
        rooms_subtitle:         'Where complaints recur',
        rooms_list:             'Most complaints',
        rooms_none:             'No complaints in this period.',
        room_open:              'open: {n}',
        room_praise:            'praise: {n}',
        room_suggestions:       'suggestions: {n}',
        response_title:         'Response',
        response_subtitle:      'How fast and when we replied to complaints',
        no_complaints:          'No complaints in this period.',
        replied:                'The guest got a reply',
        n_of_m:                 '{n} of {m}',
        reply_when:             'The reply came',
        during_stay:            'during the stay',
        after_stay:             'after departure',
        overdue_title:          'No reply for more than {h} h',
        overdue_none:           'No open complaint has waited longer than {h} h.',
        overdue_more:           'And {n} more — all of them are in the list in Overview.',
        review_none:            'No review links are set.',
        review_set:             'Set them',
        review_admin:           'An admin sets them in Settings.',
        review_no_clicks:       'No clicks in this period ({sites}).',
    },
    feedback_settings: {
        title:               'Guest feedback',
        subtitle:            'The "Praise, complaints and suggestions" tile on the TV and the page on the guest\'s phone',
        enabled:             'Guests may send feedback',
        enabled_help:        'Off: no tile and no page.',
        retention_days:      'Keep (days)',
        retention_help:      '30–1095; a change applies to stored feedback too.',
        retention_rule:      'From 30 to 1095 days.',
        max_per_day:         'At most per day',
        max_per_day_help:    'Feedback per stay per day, 1–50.',
        max_rule:            'From 1 to 50.',
        review_title:        'Rate us',
        review_subtitle:     'The links for reviewing the hotel and when the guest sees them',
        policy_title:        'Every guest is invited',
        policy:              'The review links are offered to every guest regardless of their rating or complaint — Google forbids asking only satisfied guests for reviews. A complaint goes to reception first, with the links quieter below it.',
        google_help:         'The review link from Google Maps ("Ask for reviews").',
        https_only:          'An https:// address only',
        invite:              'When we invite',
        invite_none:         'With nothing chosen the guest sees the links nowhere.',
        departure_from:      'The evening before departure from',
        departure_from_help: 'Applies to "On the departure day".',
    },
    request_source: {
        tv:        'Room TV',
        guest_app: 'Guest\'s phone',
    },
    booking_mode: {
        slots: 'The house\'s times',
        free:  'Any time in a window',
    },
    invitation_booking: {
        title:         'In-app booking',
        enabled:       'Guests can book on the TV and the phone',
        enabled_help:  'A "Book" button; reception confirms the booking in Guest requests. Charged to the room.',
        mode:          'How the guest picks a time',
        slots:         'Times',
        slots_help:    'The date and time, and at most how many persons (0 = no limit).',
        slot_add:      'Add a time',
        capacity:      'Max persons',
        capacity_help: 'Per time; 0 = no limit.',
        persons_max:   'at most {n} persons',
        no_limit:      'no limit',
        from:          'From',
        to:            'To',
        step:          'Every',
        every_minutes: 'every {n} min',
        max_persons:   'Max persons per booking',
        cutoff:        'Book at the latest (minutes before)',
        cutoff_help:   'A time disappears from the choice this many minutes before it starts.',
        slot_twice:    'A time is listed twice.',
        slots_required: 'Add at least one time.',
        window_order:  'The window must end after it starts.',
    },
    room_service_menu: {
        title:              'Room service',
        subtitle:           'The menu from Imprion POS for orders from the TV and the guest\'s phone',
        import:             'Import the menu',
        sync:               'Refresh from POS',
        synced:             'Refreshed: {items} items, {added} new, {missing} gone from the POS.',
        settings:           'Settings',
        settings_help:      'When and how guests order to the room',
        enabled:            'Room service on',
        enabled_help:       'Guests order from the TV and the phone; charged to the room.',
        hours:              'Ordering hours',
        hours_help:         'No window = all day. A window that ends before it starts runs past midnight.',
        hours_add:          'Add a window',
        clock_invalid:      'Write the time as HH:MM.',
        note:               'The menu\'s line',
        note_help:          'E.g. "Room service from 7:00 to 23:00".',
        lead_minutes:       'Shortest time to delivery (min)',
        lead_minutes_help:  'For a chosen delivery time.',
        prep_minutes:       'Usual preparation time (min)',
        prep_minutes_help:  'Offered as the estimate when the kitchen accepts an "as soon as possible" order.',
        late_minutes:       'Late after (min)',
        late_minutes_help:  'An "as soon as possible" estimate longer than this lets the guest cancel.',
        late_cancel_minutes:      'Guest may cancel for (min)',
        late_cancel_minutes_help: 'How long after the accept the guest may cancel a late order; 0 = never.',
        delivery:           'Delivery',
        delivery_help:      'Charged on every room-service order as a line of its own that the guest cannot remove.',
        delivery_mode:      'Charge for delivery',
        delivery_off:       'No',
        delivery_item:      'A POS item',
        delivery_fixed:     'A fixed amount',
        delivery_item_pick: 'Item',
        delivery_item_help: 'Any imported item, also one not offered to guests (e.g. in a group like HOTEL SERVICES). The price is the POS price.',
        delivery_item_none: 'Pick an item',
        delivery_amount:    'Amount ({currency})',
        delivery_name:      'The line\'s name for the guest',
        delivery_name_help: 'Empty = the item\'s name (a POS item) or "Delivery" in the guest\'s language.',
        delivery_free_from: 'Free from ({currency})',
        delivery_free_from_help: 'When the items reach this amount there is no charge; 0 = always.',
        max_open:           'Max open requests per room',
        max_open_help:      'Bookings and orders together.',
        max_quantity:       'Max quantity per line',
        menu:               'Menu',
        menu_help:          'Switch on the groups and items you offer. New items from the POS arrive switched on, new groups switched off.',
        menu_help_kitchen:  'Mark an item sold out when it runs out.',
        search:             'Search',
        empty:              'The menu has not been imported from Imprion POS yet.',
        items:              'Items',
        on_menu_n:          '{n} / {total} on the menu',
        group_edit:         'Menu group',
        group_from_pos:     'In the POS: {category} › {name}',
        group_name_help:    'Empty = the POS name.',
        group_offered:      'The group is on the menu',
        item_edit:          'Menu item',
        from_pos:           'Code {code} · {price} · VAT {vat}',
        guest_name:         'Name for guests',
        guest_name_help:    'Empty = the POS name.',
        description:        'Description',
        photo:              'Photo',
        offered:            'Offered to rooms',
        offered_help:       'A switched-off item is not shown to guests.',
        offered_short:      'Offered',
        available:          'Available',
        sold_out:           'Sold out',
        pos_missing:        'Gone from the POS',
        pos_disabled:       'Disabled in the POS',
        last_sync:          'Last refreshed from the POS: {when}',
    },
    pos: {
        title:             'Imprion POS',
        subtitle:          'Where the menu comes from (read only, the till is never touched)',
        base_url:          'API address',
        base_url_help:     'Empty = Imprion POS\'s default address.',
        https_only:        'The address must be https://.',
        token:             'API token',
        token_help:        'A token from the Imprion POS till (External API).',
        token_set:         'A token is stored. An empty field keeps it.',
        business_unit:     'Business unit',
        business_unit_help:'Whose products are on the menu. "Test" lists them.',
        disabled:          'disabled',
        test:              'Test',
        test_ok:           'The connection works: {n} business units.',
        test_failed:       'The connection does not work.',
    },
    milo: {
        figure:                  'Milo, the Imprion TV assistant',
        title:                   'Milo',
        intro:                   'Ask about this space’s setup or how to do something in the console.',
        intro_kitchen:           'Ask about room-service orders — Milo accepts, delivers or declines them once you confirm.',
        intro_housekeeping:      'Ask about guest requests — Milo accepts, completes or declines them once you confirm.',
        open:                    'Ask Milo',
        new_conversation:        'New conversation',
        not_configured_title:    'Milo is not set up yet',
        not_configured_help:     'The provider has not entered an AI key yet, or Milo is switched off for the whole installation.',
        open_provider_settings:  'Open provider settings',
        thinking:                'Thinking …',
        looking_up:              'Looking it up …',
        placeholder:             'Ask Milo …',
        send:                    'Send',
        left_today:              '{n} message left today | {n} messages left today',
        tool: {
            overview:         'Checking the overview …',
            list_devices:     'Checking the devices …',
            list_rooms:       'Checking the rooms …',
            list_channels:    'Checking the channels …',
            home_tiles:       'Checking the home tiles …',
            list_invitations: 'Checking the invitations …',
            screen_themes:    'Checking the screen themes …',
            find_local_events:   'Looking for events nearby …',
            propose_local_event: 'Preparing an event proposal …',
            transit_departures:  'Checking public transport …',
            travel_time:         'Working out the travel time …',
        },
        error_rate:              'Too many messages in a short time — wait a moment.',
        error_not_configured:    'Milo is not set up yet.',
        error_unavailable:       'Milo is not available right now.',
        error_budget:            'Today’s budget for Milo is used up — try again tomorrow.',
        error_limit:             'You have asked Milo a lot today — try again tomorrow.',
        error_full:              'This conversation is long — start a new one.',
        error_failed:            'Milo cannot connect right now. Try again.',
        error_closed:            'This proposal is no longer valid.',
        error_busy:              'Milo is still answering your previous question.',
        error_voice_unavailable: 'Voice is not available.',
        error_voice_failed:      'The voice service did not answer.',
        error_network:           'Could not reach the server.',
        state_pending:           'Awaiting confirmation',
        state_done:              'Done',
        state_failed:            'Failed',
        state_cancelled:         'Cancelled',
        state_expired:           'Expired',
        pms_conflict:            'The PMS reports a stay for this room — the room TV shows the PMS stay.',
        proposal_feedback_reply:  'Reply to the guest',
        proposal_feedback_status: 'Feedback status',
        proposal_reply_language:  'Language: {language}',
        proposal_then_status:     'Then status: {status}',
        proposal_new_status:      'New status: {status}',
        proposal_note:            'Internal note',
        proposal_reply_hint:      'The guest gets it on their phone (and on the room TV while still in the house) only once you confirm.',
        proposal_local_event:     'Event for Okolica',
        event_at:                 '{day} at {time}',
        event_source:             'Source:',
        event_picture_source:     'Picture: the organiser\'s page',
        event_picture_ai:         'Picture: made by Milo (AI)',
        event_picture_later:      'You can add the photo in Vsebina › Hotel.',
        event_picture_use_source: 'Page picture',
        event_picture_use_ai:     'AI picture',
        event_picture_regenerate: 'Another AI picture',
        event_picture_remove:     'No picture',
        proposal_remove_local_event: 'Remove event from Okolica',
        event_past:               'past',
        event_remove_note:        'This event will be taken off Okolica.',
        proposal_seasonal_tiles:  'Decorations on tile pictures',
        season_tiles_new:         'A new seasonal theme with only the tile pictures, based on {base}.',
        season_tiles_skipped:     'Skipped: {list}',
        season_tiles_note:        'The hotel’s pictures stay; the decorated ones show only in the theme’s period. Making them takes a few minutes.',
        event_translating:        'Milo is translating into the hotel\'s languages and saving …',
    },
    milo_surface: {
        guest:   'Guest phone',
        screen:  'Room TV',
        console: 'Console',
    },
    milo_language: {
        sl: 'Slovenian',
        en: 'English',
        de: 'German',
        it: 'Italian',
        hr: 'Croatian',
        hu: 'Hungarian',
    },
    milo_error_kind: {
        credit:      "The provider account has no credit left — top it up in the provider's console.",
        auth:        'The provider does not accept the key: it is wrong or was revoked.',
        rate_limit:  'The provider is limiting requests — try again in a few minutes.',
        overloaded:  'The provider is overloaded right now — try again later.',
        server:      "An error on the provider's side.",
        request:     'The provider refused the request (e.g. the model is not available to this key).',
        timeout:     'The provider did not answer in time.',
        unreachable: 'The provider cannot be reached (network or DNS).',
    },
    milo_gender: {
        male:    'male',
        female:  'female',
        neutral: 'neutral',
    },
    milo_ai_provider: {
        anthropic: 'Anthropic (Claude)',
        openai:    'OpenAI (GPT)',
        soniox:    'Soniox',
        tomtom:    'TomTom',
    },
    milo_settings: {
        title:                    'Milo',
        subtitle:                 'The AI assistant for guests on their phone and TV, and for staff in the console.',
        tab_settings:             'Settings',
        tab_usage:                'Usage',
        not_configured_tenant:    'The provider has not entered an AI key yet. You can prepare the settings now; Milo starts working once the key is in.',
        where:                    'Where Milo is available',
        where_help:               'Three independent switches — on the guest phone and the room TV Milo stays off until you switch it on.',
        phone_enabled:            'In the guest app (phone)',
        phone_enabled_help:       'The guest chats with Milo in the app opened from the QR code on the TV.',
        tv_enabled:               'On the room TVs',
        tv_enabled_help:          'A Milo tile with suggested questions appears on the TV’s home screen.',
        console_enabled:          'In the staff console',
        console_enabled_help:     'The Milo button in the console’s top bar.',
        figure:                   'Look',
        figure_help:              'How Milo is drawn on the room TV, in the guest app and in the console.',
        figure_3d:                'Moving 3D head',
        figure_3d_help:           'Off: Milo is shown with still poses, which still change with what he is doing. Suits weaker TVs.',
        traffic:                  'Traffic (TomTom)',
        traffic_help:             'Milo tells how long a drive takes with the traffic, and warns of jams, accidents, roadworks and closures nearby or on the way.',
        traffic_not_configured:   'The provider has not entered a TomTom key yet — Milo gives drive times without traffic.',
        traffic_enabled:          'Milo uses the traffic',
        traffic_enabled_help:     'Off: Milo reads no traffic data for this hotel.',
        instructions:             'House instructions',
        instructions_help:        'Tone, what Milo should recommend and what not to talk about. Instructions cannot widen what Milo may do.',
        instructions_placeholder: 'E.g.: Always recommend dinner in our restaurant. Be informal. Do not discuss room prices.',
        voice:                    'Voice',
        voice_help:               'Milo can speak its answers and listen on the phone (Soniox).',
        voice_not_configured:     'The provider has not entered a voice key (Soniox) yet, so voice is hidden.',
        tts_tv:                   'Milo speaks on the TV',
        tts_tv_help:              'Answers play on the room TV at the TV’s volume.',
        tts_phone:                'Milo speaks on the phone',
        tts_phone_help:           'The guest can mute the sound in the app.',
        stt_phone:                'Microphone on the phone',
        stt_phone_help:           'The guest speaks the question instead of typing it.',
        speed:                    'Speech speed',
        speed_help:               'From 0.70 to 1.30 (1.00 = normal).',
        tts_characters:           'Spoken characters per stay per day',
        tts_characters_help:      'Once used up, answers are text only. 0 = no speech.',
        voices:                   'Voice per language',
        voices_reload:            'Reload voices',
        voice_default:            'The provider’s default voice',
        voice_cloned:             'cloned',
        limits:                   'Daily limits',
        limits_help:              'They count messages; a new conversation does not reset them.',
        stay_daily:               'Guest messages per day',
        stay_daily_help:          'Per stay, phone and TV together.',
        user_daily:               'Staff messages per day',
        tenant_daily:             'All messages of the space per day',
        retention:                'Keep conversations (days)',
        retention_help:           'Messages are deleted after this many days. A change applies to new messages.',
        turns:                    'Conversation length',
        turns_help:               'How many questions one conversation with Milo holds.',
        guest_turns:              'Guest questions per conversation',
        guest_turns_help:         'The most questions in one conversation with Milo — on the phone and the TV. When the guest has used them up, they start a new conversation.',
        console_turns:            'Staff questions per conversation',
        console_turns_help:       'The most questions in one conversation with Milo in the console. When they are used up, a new conversation starts.',
        models:                   'Models',
        models_help:              'The models are chosen by the provider.',
        models_help_provider:     'Empty = the provider’s model. An override applies to this space only (same provider).',
        guest_model:              'Guest model',
        console_model:            'Console model',
        effective:                'In use: {model}',
        model_default:            'the provider’s model',
        model_invalid:            'Invalid model name.',
        range:                    'Between {min} and {max}.',
        preview:                  'Try',
        preview_stop:             'Stop',
        preview_aria:             'Try the voice: {language}',
        preview_text:             'Text to try',
        preview_text_help:        "Empty = Milo's greeting in the hotel's name, in the voice's language. Uses the speech speed as set now, saved or not.",
        preview_text_placeholder: 'E.g.: Breakfast is served from 7 to 10 in the ground-floor restaurant.',
        preview_unplayable:       'The browser cannot play the sample.',
    },
    milo_usage: {
        window:         'From {from} to {to}',
        last_days:      'Last {n} days',
        tokens_line:    'Tokens: {input} in · {output} out',
        voice_line:     'Voice: {characters} characters · microphone {sessions}×',
        by_day:         'By day',
        empty:          'Nobody used Milo in this period.',
        date:           'Day',
        surface:        'Where',
        messages:       'Messages',
        calls:          'Model calls',
        input_tokens:   'Input tokens',
        output_tokens:  'Output tokens',
        cache_tokens:   'Cache',
        tts_characters: 'Spoken characters',
        stt:            'Microphone',
        stt_cell:       '{sessions}× · {seconds} s',
        errors:         'Errors',
        by_tenant:      'Usage by tenant',
        by_tenant_help: 'Every tenant over the chosen period, the most active first.',
        tenant:         'Tenant',
    },
    milo_provider: {
        title:                 'Milo — AI providers',
        subtitle:              'Keys, models and voice for every tenant of this installation.',
        tab_ai:                'Reasoning',
        tab_voice:             'Voice',
        tab_usage:             'Usage',
        tab_traffic:           'Traffic',
        master:                'Milo for the whole installation',
        master_help:           'Switching it off stops Milo everywhere, whatever the tenants’ settings.',
        enabled:               'Milo is on',
        keys:                  'API keys',
        keys_help:             'A key is stored encrypted and never shown again — only its last four characters are.',
        models:                'Provider and model',
        models_help:           'For each group separately. Pick a model from the list (after testing the connection) or type it.',
        group_guest:           'Guests (phone and TV)',
        group_console:         'Console (staff)',
        model:                 'Model',
        model_no_key:          'No key is stored for this provider — Milo will not work here.',
        model_help:            'Empty = {model}',
        image_model:           'Event picture model (OpenAI)',
        image_model_help:      'Empty = {model}. Milo uses it to make an event\'s picture when the event\'s page has none; it needs the OpenAI key.',
        budget:                'Daily token budget',
        budget_help:           'Input and output tokens per day (UTC), all tenants together. 0 = no cap.',
        soniox:                'Soniox',
        soniox_help:           'Speech to text (the phone’s microphone) and text to speech (TV and phone). The key’s region is detected by itself.',
        voice_models:          'Voice models',
        voice_models_help:     'Empty = the default model. The list fills after testing the connection.',
        stt_model:             'Speech to text (live)',
        stt_async_model:       'Speech to text (recording)',
        stt_async_help:        'For browsers that cannot stream audio.',
        tts_model:             'Text to speech',
        default_voice:         'Default voice',
        default_voice_help:    'Used where a tenant has not chosen its own voice for a language. The list includes the project’s cloned voices.',
        voices_need_key:       'Save a Soniox key first.',
        voice_placeholder:     'Choose a voice',
        voice_languages:       'The model’s languages: {list}',
        soniox_rejected:       'No region accepted the Soniox key.',
        key_removing:          'Removed on save',
        key_set:               'Key is set {hint}',
        key_unset:             'Not set',
        region:                'Region {region}',
        key_remove:            'Remove',
        key_keep:              'Keep',
        key_replace_help:      'Type a new key to replace the stored one; empty keeps it.',
        key_help:              'The key is stored when you save.',
        test:                  'Test connection',
        test_ok:               'Connection works — models: {count}',
        test_failed:           'Connection failed',
        pick_model:            'Pick a model',
        pick_model_test_first: 'Test the connection first to list the models',
        last_error:            "The provider's last error",
        last_error_meta:       '{time} · {where}',
        last_error_status:     'HTTP {status}: {message}',
        last_error_cleared:    'It goes away with the next successful call or connection test. Guests only see "Milo cannot connect right now".',
        where_guest:           "guest's phone",
        where_screen:          'room TV',
        where_console:         'console',
        where_settings:        'settings',
        preview_language:      'Language to try',
        preview_text_help:     "Empty = Milo's greeting in the chosen language. Uses the model and voice as entered, saved or not.",
        traffic:               'Traffic (TomTom)',
        traffic_help:          'One TomTom account for every hotel: Milo uses it for drive times with traffic and for jams, accidents, roadworks and closures. Create the key at developer.tomtom.com.',
        tomtom_ok:             'The connection works — the key is accepted',
        traffic_usage:         'This month: routes {routing} / {routing_free}, incidents {incidents} / {incidents_free}, searches {geocode} / {geocode_free}',
        traffic_usage_help:    'Requests this month (UTC) against TomTom\'s free tier. Milo stops earlier — at {routing} routes, {incidents} incidents and {geocode} searches — and tells the guest the traffic data is not available right now.',
    },
    stay_state: {
        reserved: 'Reserved',
        in_room:  'In room',
        departed: 'Checked out',
    },
    stay_action: {
        created:     'Entered',
        updated:     'Changed',
        checked_in:  'Check-in',
        checked_out: 'Check-out',
    },
    occupancy: {
        title:              'Occupancy',
        subtitle:           'Who is in which room: arrivals, departures and guests. On arrival the room TV greets the guest; on departure it resets for the next one.',
        room:               'Room',
        guest:              'Guest',
        new_stay:           'New guest',
        new_stay_help:      'A stay — the room TV is not touched until the arrival.',
        settings:           'Settings',
        settings_help:      'Check-in and check-out times, automatic check-in and check-out.',
        today:              'Today',
        week_back:          'A week back',
        week_forward:       'A week forward',
        zoom_two_weeks:     '2 weeks',
        zoom_month:         'Month',
        search:             'Search a room or guest',
        only_free_tonight:  'Free tonight only',
        free_tonight:       'Free tonight',
        tonight:            'Tonight {occupied} / {rooms} taken',
        from_pms:           'From PMS',
        conflict:           'Conflict with PMS',
        conflicts:          '{n} stay overlaps the PMS | {n} stays overlap the PMS',
        conflict_help:      'The PMS stay wins — the room TV shows the PMS guest. Delete or move the manual stay.',
        pms_failed:         'The PMS did not answer — only the stays entered here are shown.',
        pms_guest:          'Guest (PMS)',
        pms_stay:           'Stay from the PMS',
        unassigned:         'Unassigned (PMS)',
        unassigned_room:    '{type} — no room assigned yet',
        unassigned_help:    'The PMS has not given this stay a room yet: Occupancy shows it under its room type, the TV does not.',
        pms_unmapped:       '{n} PMS stay is in a room not linked to ours | {n} PMS stays are in rooms not linked to ours',
        pms_unmapped_help:  'Link the rooms on the PMS link page — only then do they show here and on the TV.',
        open_connector:     'PMS link',
        pms_read_only:      'This stay comes from the PMS and cannot be changed here.',
        arrival:            'Arrival',
        arrival_unknown:    'not known (current stay)',
        departure:          'Departure',
        departure_rule:     'The departure must be after the arrival.',
        first_name:         'First name',
        first_name_help:    'On the TV: "Welcome, {name}".',
        first_name_help_empty: 'The room TV greets the guest by this name.',
        last_name:          'Last name',
        language:           'Language',
        language_help:      'The room TV will be in this language.',
        language_house:     "The house's language",
        guests:             'Guests',
        note:               'Note',
        note_help:          'For reception only — never on the TV or the phone.',
        nights:             '{n} night | {n} nights',
        check_in:           'Check in',
        check_out:          'Check out',
        check_in_help:      'Check-in prepares the room for the guest: a new QR code, the name and language on the TV.',
        check_out_help:     'Check-out resets the TV for the next guest (apps, alarm, language, a new QR code).',
        confirm_check_in:   'Check {name} in to {room}? The room TV greets them within a minute.',
        confirm_check_out:  'Check {name} out of {room}? The TV resets for the next guest.',
        checked_in:         'The guest is checked in.',
        checked_out:        'The guest is checked out, the TV is resetting.',
        checked_in_at:      'checked in {at}',
        checked_out_at:     'checked out {at}',
        delete:             'Delete the stay',
        confirm_delete:     'Delete the reservation for {name}?',
        history:            'History',
        by_auto:            'automatically',
        read_only:          'Only an administrator and reception may change stays.',
        departed_read_only: 'A checked-out stay is the record — it no longer changes.',
        clash:              'Another guest has this room then.',
        clash_short:        'Taken',
        retry:              'Try again',
        no_rooms:           'The hotel has no rooms yet — add them on the Rooms page.',
        no_match:           'No room matches the filter.',
        hint_mouse:         'Drag across free days to enter a stay, drag an end to lengthen it, drag a bar to another room or other days, click to edit.',
        hint_touch:         'Tap a free day to enter a stay or a bar to edit it; the end of a bar can be dragged.',
        check_in_time:      'Check-in time',
        check_out_time:     'Check-out time',
        auto_check_in:      'Automatic check-in',
        auto_check_in_help: 'On the arrival day at the check-in time the TV is prepared for the guest.',
        auto_check_out:     'Automatic check-out',
        auto_check_out_help: 'On the departure day at the check-out time the TV resets for the next guest.',
        retention:          'Keep days after departure',
        retention_help:     'Then the stay is deleted (30–3650 days).',
        card_title:         'Occupancy today',
        open_chart:         'Occupancy',
        rooms_occupied:     'rooms taken tonight',
        arrivals_today:     'arrivals today',
        departures_today:   'departures today',
        field: {
            room_id:    'room',
            arrival:    'arrival',
            departure:  'departure',
            first_name: 'first name',
            last_name:  'last name',
            language:   'language',
            guests:     'guests',
            note:       'note',
        },
    },
} satisfies MessageSchema

export default en
